import { clearPeopleRegistrationDraft, readPeopleRegistrationDraft, savePeopleRegistrationDraft } from '../model/peopleRegistrationDraft';
import { PersonChangeHistory } from '../profile/PersonChangeHistory';
import { PeopleImportDialog } from '../components/PeopleImportDialog';
import { PeopleRegistrationOutcome } from '../components/PeopleRegistrationOutcome';
import { BulkOperationsWorkspace, type BulkOperationsWorkspaceProps, type BulkOperationsPort } from '../../bulk/public';
import { BusinessTitleField, personnelActionMatchesStatus, personnelStatusLabel, WorkerPersonnelActionDialog, WorkerRelationshipTransitionDialog, type WorkerTransitionKind, type WorkerPersonnelAction, type StaffingOperationsPort, type StaffingWorker } from '../../staffing/public';
import { EmploymentContractChangesDialog, type EmploymentContractApi } from '../../rewards/public';
import { PeopleSavedViews } from '../components/PeopleSavedViews';
import { PeopleBulkChangeDialog } from '../components/PeopleBulkChangeDialog';
import { collectPeopleIssues, filterPeople, peopleOrganizationOptions, peopleIssueOptions, type PeopleFilters, type PeopleViewPreferencesPort } from '../operations/peopleWorkspace';
import { useEffect, useId, useMemo, useRef, useState, type FocusEvent, type ReactNode } from 'react';
import { AddOutlined, ArrowBackOutlined, ChevronRightOutlined, ExpandMoreRounded, DownloadOutlined } from '@mui/icons-material';
import {
  Accordion, AccordionSummary, AccordionDetails,
  Alert,
  Box,
  Button,
  IconButton,
  Divider,
  ListSubheader,
  ListItemText,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import type { GridColDef, GridRowId } from '@mui/x-data-grid';
import {
  AnchoredMenu,
  ChoiceControl,
  IdentityAvatar,
  DefinitionGrid,
  CompactDateField,
  DialogSurface,
  FlowSteps,
  FormErrorSummary,
  FormGrid,
  FormGridItem,
  FormSection,
  FormTextField,
  FormAutocomplete,
  useConfirmation,
  FeedbackSnackbar,
  useFeedbackSnackbar,
  InformationPage,
  LoadingIndicator,
  ResourcePage,
  ResourceCollection,
  ResourceState,
  ResultState,
  StatusBadge,
  TableIdentityCell,
  ValueBadge,
  designTokens,
  type DefinitionGridItem,
  type FormErrorSummaryItem,
  type ResourceCollectionState,
} from '../../../design-system';
import type {
  StaffingChangeSummary,
  WorkdayReference,
  WorkerProfileWorker,
} from '../profile/model';
import { useAsyncResource } from '../../../shared/useAsyncResource';
import { formatPersonDisplayName } from '../../../shared/formatPersonDisplayName';
import {
  fixturePeopleHireReferences,
  fixturePeopleJobTypes,
  fixturePeopleStaffingChanges,
  fixturePeopleWorkers,
  fixturePeopleWorkSpaces,
} from '../operations/fixtures';
import type { HireOrganization, HireReference, HireReferences, PeopleAccess } from '../operations/model';
import type { PeopleOperationsPort, PeopleRoutePort } from '../operations/port';
import { ContactChangeDialog, type ContactChangeWorkflowReference } from '../profile/ContactChangeDialog';
import { ExternalCareerSection } from '../profile/ExternalCareerSection';
import type { PersonNameProfile, WorkerEmploymentProfile, WorkerPrivateProfile } from '../profile/model';
import { PersonFamilySection } from '../profile/PersonFamilySection';
import { PersonNameChangeDialog } from '../profile/PersonNameChangeDialog';
import type { PeopleProfilePort } from '../profile/port';
import { ProfilePhotoControl } from '../profile/ProfilePhotoControl';
import { WorkerProfile } from '../profile/WorkerProfile';
import { workerStatusPresentation } from '../profile/workerStatusPresentation';

type PersonnelSession = {
  id: string;
  open: boolean;
  worker: StaffingWorker;
  asOf: string;
  action?: WorkerPersonnelAction;
  kind?: WorkerTransitionKind;
  correction?: { id: string; effectiveDate?: string };
};

function WorkerAvatar({ worker }: { worker: WorkerProfileWorker }) {
  return <IdentityAvatar src={worker.photoUrl} sx={{ width: 28, height: 28, fontSize: 11, bgcolor: designTokens.color.subtleSurface, color: worker.status === '재직' ? 'primary.main' : 'text.secondary' }}>{worker.initials}</IdentityAvatar>;
}

function selectedHireReference(id: string, references: readonly { id: string; descriptor?: string }[]): WorkdayReference | undefined {
  if (!id) return undefined;
  const selected = references.find((reference) => reference.id === id);
  return selected?.descriptor ? { id: selected.id, descriptor: selected.descriptor } : { id };
}

function optionalRegistrationDetail(label: string, value: string): DefinitionGridItem[] {
  return value.trim() ? [{ label, value: value.trim() }] : [];
}

function organizationPath(organization: HireOrganization, organizations: readonly HireOrganization[]): string {
  const names = [organization.descriptor || '이름 없는 조직'];
  const seen = new Set([organization.id]);
  let parent = organization.parent;
  while (parent && !seen.has(parent.id)) {
    seen.add(parent.id);
    const ancestor = organizations.find((item) => item.id === parent?.id);
    if (ancestor?.descriptor || parent.descriptor) names.unshift(ancestor?.descriptor || parent.descriptor!);
    parent = ancestor?.parent;
  }
  return names.join(' / ');
}

function HireReferenceField({ id, label, value, options, onChange, required = false, disabled = false, error, helperText, help }: {
  id?: string; label: string; value: string; options: readonly { id: string; descriptor?: string }[];
  onChange: (id: string) => void; required?: boolean; disabled?: boolean; error?: boolean; helperText?: string; help?: string;
}) {
  return <FormAutocomplete id={id} options={options} value={options.find((item) => item.id === value) ?? null}
    getOptionKey={(item) => item.id} getOptionLabel={(item) => item.descriptor || '이름 미등록'}
    isOptionEqualToValue={(option, selected) => option.id === selected.id}
    onChange={(_, selected) => onChange(selected?.id ?? '')} disabled={disabled}
    input={{ label, required, error, helperText, help, placeholder: '검색하여 선택' }} />;
}

function fixturePersonNameProfile(worker: WorkerProfileWorker): PersonNameProfile {
  const legalName = worker.id === '02' ? '박진우' : worker.name;
  const characters = Array.from(legalName.trim());
  return {
    person: { id: worker.personId, descriptor: worker.name },
    legalName: {
      fullName: legalName,
      lastName: characters[0] ?? '',
      firstName: characters.slice(1).join(''),
      countryCode: 'KR',
      locale: 'ko-KR',
    },
    preferredName: { fullName: worker.id === '02' ? '우디' : worker.name, countryCode: 'KR', locale: 'ko-KR' },
    version: 2,
  };
}

function fixtureWorkerPrivateProfile(worker: WorkerProfileWorker): WorkerPrivateProfile {
  if (worker.id === '02') {
    return {
      personId: worker.personId,
      birthDate: '1990-02-14',
      gender: { id: 'MALE', descriptor: '남성' },
      maritalStatus: { id: 'SINGLE', descriptor: 'Single' },
      homeEmails: [{ id: 'home-email-1', emailAddress: 'woody.park@example.net', usageType: 'PERSONAL', primary: true, public: false, verificationStatus: 'VERIFIED' }],
      homePhones: [
        { id: 'home-phone-mobile', phoneNumber: '010-9876-2104', usageType: 'MOBILE', primary: true, public: false, verificationStatus: 'VERIFIED' },
        { id: 'home-phone-landline', phoneNumber: '02-6203-0104', usageType: 'HOME', primary: false, public: false, verificationStatus: 'VERIFIED' },
      ],
      homeAddresses: [{ id: 'home-address-1', country: { id: 'KR', descriptor: '대한민국' }, region: '서울특별시', city: '성동구', postalCode: '04790', addressLines: ['왕십리로 83-21, 101동 1204호'], usageType: 'HOME', primary: true, public: false }],
      emergencyContacts: [
        {
          id: 'emergency-contact-1', name: '박선영', relationshipType: 'PARENT', priority: 1,
          phoneNumbers: [{ phoneNumber: '010-5555-2104', usageType: 'MOBILE', primary: true }],
          emailAddresses: [{ emailAddress: 'sunyoung.park@example.net', usageType: 'PERSONAL', primary: true }],
        },
        {
          id: 'emergency-contact-2', name: '김현수', relationshipType: 'FRIEND', priority: 2,
          phoneNumbers: [
            { phoneNumber: '010-7777-2104', usageType: 'MOBILE', primary: true },
            { phoneNumber: '02-6203-7777', usageType: 'WORK' },
          ],
        },
      ],
      emergencyContactsLoaded: true,
      workEmails: [{ id: 'work-email-1', emailAddress: worker.email, usageType: 'WORK', primary: true, public: true, verificationStatus: 'VERIFIED' }],
      workPhones: [
        { id: 'work-phone-desk', phoneNumber: '02-6203-2104', extension: '2104', usageType: 'WORK', primary: true, public: true, verificationStatus: 'VERIFIED' },
        { id: 'work-phone-mobile', phoneNumber: worker.phone, usageType: 'MOBILE', primary: false, public: false, verificationStatus: 'VERIFIED' },
      ],
      canReadPersonalData: true,
      personalDataLoaded: true,
      canReadHomeContact: true,
      canReadHomeContactFull: true,
      canReadWorkContact: true,
      canReadWorkContactFull: true,
      homeContactLoaded: true,
      workContactLoaded: true,
    };
  }
  return {
    personId: worker.personId,
    canReadPersonalData: true,
    personalDataLoaded: true,
    canReadHomeContact: true,
    canReadHomeContactFull: true,
    canReadWorkContact: true,
    canReadWorkContactFull: true,
    homeContactLoaded: true,
    workContactLoaded: true,
    emergencyContactsLoaded: true,
  };
}

function fixtureWorkerEmploymentProfile(worker: WorkerProfileWorker): WorkerEmploymentProfile {
  return { workerId: worker.id, canReadContract: true, contractType: worker.workerTypeId === 'CONTINGENT_WORKER' ? undefined : 'PERMANENT' };
}

type SensitiveResourceResult<T> = { value?: T; forbidden?: boolean; missing?: boolean; error?: Error };

async function settleSensitiveResource<T>(request: Promise<T>): Promise<SensitiveResourceResult<T>> {
  try {
    return { value: await request };
  } catch (error) {
    if (httpStatus(error) === 403) return { forbidden: true };
    if (httpStatus(error) === 404) return { missing: true };
    return { error: error instanceof Error ? error : new Error('민감정보를 불러오지 못했습니다.') };
  }
}

function settleSensitiveResourceWhen<T>(enabled: boolean, request: () => Promise<T>): Promise<SensitiveResourceResult<T>> {
  return enabled ? settleSensitiveResource(request()) : Promise.resolve({ forbidden: true });
}

function httpStatus(error: unknown) {
  return error && typeof error === 'object' && 'status' in error && typeof error.status === 'number'
    ? error.status
    : undefined;
}

export type PeoplePageProps = {
  port: PeopleOperationsPort;
  profilePort: PeopleProfilePort;
  route: PeopleRoutePort;
  access: PeopleAccess;
  fixtureMode: boolean;
  bulkPort?: BulkOperationsPort;
  staffingPort?: StaffingOperationsPort;
  contractApi?: EmploymentContractApi;
  preferences?: PeopleViewPreferencesPort;
  recoveryScope?: string;
  onOpenBulkHistory?: () => void;
  bulkWorkspace?: Omit<BulkOperationsWorkspaceProps, 'children' | 'onCompleted'>;
};

export function PeoplePage(props: PeoplePageProps) {
  const [bulkRevision, setBulkRevision] = useState(0);
  if (!props.bulkWorkspace) return <PeoplePageContent {...props} />;
  return <BulkOperationsWorkspace {...props.bulkWorkspace} onCompleted={() => setBulkRevision((value) => value + 1)}>
    {(bulk) => <PeoplePageContent {...props} bulkRevision={bulkRevision}
      route={{ ...props.route, openBulkHire: () => bulk.openCreate('EMPLOYEE_HIRE'), openBulkJob: bulk.openJob }}
      onOpenBulkHistory={props.bulkWorkspace?.canRead ? bulk.openHistory : undefined}
    />}
  </BulkOperationsWorkspace>;
}

function PeoplePageContent({ port, profilePort, route, access, fixtureMode, bulkPort, staffingPort, contractApi, preferences, recoveryScope, onOpenBulkHistory, bulkRevision = 0 }: PeoplePageProps & { bulkRevision?: number }) {
  const { notify, feedbackProps } = useFeedbackSnackbar();
  const today = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(new Date());
  const [asOf, setAsOf] = useState(() => {
    return route.read().asOf ?? today;
  });
  const resource = useAsyncResource(
    () => fixtureMode ? Promise.resolve(fixturePeopleWorkers) : port.listWorkers(asOf),
    [asOf, port, fixtureMode, bulkRevision],
  );
  const staffingHistory = useAsyncResource(
    () => fixtureMode ? Promise.resolve(fixturePeopleStaffingChanges) : port.listStaffingChanges(),
    [port, fixtureMode, bulkRevision],
  );
  const [fixtureNameProfiles, setFixtureNameProfiles] = useState<Record<string, PersonNameProfile>>({});
  const values = useMemo(() => {
    const current = resource.data ?? [];
    if (!fixtureMode) return current;
    return current.map((worker) => {
      const profile = fixtureNameProfiles[worker.personId];
      if (!profile) return worker;
      const name = formatPersonDisplayName(profile.preferredName?.fullName, profile.legalName.fullName, worker.name);
      return { ...worker, name, initials: Array.from(name).slice(0, 2).join('') };
    });
  }, [fixtureMode, fixtureNameProfiles, resource.data]);
  const [detailWorkerID, setDetailWorkerID] = useState(() => route.read().workerId);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [organizationFilter, setOrganizationFilter] = useState('');
  const [organizationScope, setOrganizationScope] = useState('descendants');
  const organizationResource = useAsyncResource(() => fixtureMode ? Promise.resolve(fixturePeopleHireReferences.organizations) : port.listOrganizations?.(asOf) ?? Promise.resolve([]), [port, fixtureMode, asOf]);
  const organizationOptions = peopleOrganizationOptions(values, organizationResource.data ?? []);
  const [locationFilter, setLocationFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [workerTypeFilter, setWorkerTypeFilter] = useState('');
  const [issueFilter, setIssueFilter] = useState('');
  const [selection, setSelection] = useState<GridRowId[]>([]);
  const [selectionOpen, setSelectionOpen] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const clearSelection = () => {
    const container = tableContainerRef.current;
    const focusTarget = container?.querySelector<HTMLInputElement>('[role="grid"] [role="columnheader"] input[type="checkbox"]')
      ?? container?.querySelector<HTMLInputElement>('[role="search"] input');
    // 사라질 작업바에서 목록으로 먼저 복귀합니다. 조회 중이면 남아 있는 검색 입력을 사용합니다.
    focusTarget?.focus({ preventScroll: true });
    setSelection([]);
  };
  const [bulkChangeWorkers, setBulkChangeWorkers] = useState<WorkerProfileWorker[]>();
  const [personnelSessions, setPersonnelSessions] = useState<PersonnelSession[]>([]);
  const personnelRefreshPending = useRef(false);
  const [personnelAnchor, setPersonnelAnchor] = useState<HTMLElement | null>(null);
  const personnelMenuId = useId();
  const [contractOpen, setContractOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [registrationAnchor, setRegistrationAnchor] = useState<HTMLElement | null>(null);
  const registrationMenuId = useId();
  const [savedViewsOpen, setSavedViewsOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [listError, setListError] = useState('');
  const filters: PeopleFilters = { query, status: statusFilter, organization: organizationFilter, organizationScope, location: locationFilter, company: companyFilter, workerType: workerTypeFilter, issue: issueFilter };
  const applyFilters = (next: PeopleFilters) => {
    setQuery(next.query); setStatusFilter(next.status); setOrganizationFilter(next.organization); setOrganizationScope(next.organizationScope || 'descendants'); setLocationFilter(next.location);
    setCompanyFilter(next.company); setWorkerTypeFilter(next.workerType); setIssueFilter(next.issue);
  };
  const [contactOpen, setContactOpen] = useState(false);
  const [contactSection, setContactSection] = useState<'HOME' | 'WORK' | 'EMERGENCY'>();
  const [contactNotice, setContactNotice] = useState<string>();
  const [contactWorkflows, setContactWorkflows] = useState<readonly ContactChangeWorkflowReference[]>([]);
  const [nameOpen, setNameOpen] = useState(false);
  const [recoveredDraft] = useState(() => access.canCreatePerson ? readPeopleRegistrationDraft(recoveryScope) : undefined);
  const [createOpen, setCreateOpen] = useState(false);
  const [hasCreateDraft, setHasCreateDraft] = useState(Boolean(recoveredDraft));
  const [identityExpanded, setIdentityExpanded] = useState(() => recoveredDraft?.identityExpanded ?? false);
  const [privateExpanded, setPrivateExpanded] = useState(() => recoveredDraft?.privateExpanded ?? false);
  const pendingPersonFieldFocus = useRef<{ field: string; section: 'identity' | 'private' } | undefined>(undefined);
  const [assignmentExpanded, setAssignmentExpanded] = useState(() => recoveredDraft?.assignmentExpanded ?? false);
  const [retryOnly, setRetryOnly] = useState(() => recoveredDraft?.retryOnly ?? false);
  const createPending = useRef(false);
  const { confirm, confirmationDialog } = useConfirmation();
  const [submittedSummary, setSubmittedSummary] = useState<{ name: string; hireDate: string; organization?: string; workerNumber?: string }>();
  const [createStep, setCreateStep] = useState(() => recoveredDraft?.createStep ?? 0);
  const createFormId = useId();
  const createFieldId = (field: string) => `${createFormId}-${field}`;
  const [createErrorFocusRequest, setCreateErrorFocusRequest] = useState(0);
  const [personStepValidated, setPersonStepValidated] = useState(false);
  const [hireStepValidated, setHireStepValidated] = useState(false);
  const [personTouchedFields, setPersonTouchedFields] = useState<Record<string, boolean>>({});
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string>();
  const [createNotice, setCreateNotice] = useState<string>();
  const [createWorkflow, setCreateWorkflow] = useState<WorkdayReference | undefined>(recoveredDraft?.createWorkflow);
  const [submittedWorkflow, setSubmittedWorkflow] = useState<WorkdayReference>();
  const [createdPersonID, setCreatedPersonID] = useState(() => recoveredDraft?.createdPersonID ?? '');
  const [createdHireID, setCreatedHireID] = useState(() => recoveredDraft?.createdHireID ?? '');
  const [legalFamilyName, setLegalFamilyName] = useState(() => recoveredDraft?.legalFamilyName ?? '');
  const [legalGivenName, setLegalGivenName] = useState(() => recoveredDraft?.legalGivenName ?? '');
  const [legalEnglishFamilyName, setLegalEnglishFamilyName] = useState(() => recoveredDraft?.legalEnglishFamilyName ?? '');
  const [legalEnglishGivenName, setLegalEnglishGivenName] = useState(() => recoveredDraft?.legalEnglishGivenName ?? '');
  const [nicknameKorean, setNicknameKorean] = useState(() => recoveredDraft?.nicknameKorean ?? '');
  const [nicknameEnglish, setNicknameEnglish] = useState(() => recoveredDraft?.nicknameEnglish ?? '');
  const [systemID, setSystemID] = useState(() => recoveredDraft?.systemID ?? '');
  const [birthDate, setBirthDate] = useState(() => recoveredDraft?.birthDate ?? '');
  const [gender, setGender] = useState<'MALE' | 'FEMALE' | ''>(() => recoveredDraft?.gender ?? '');
  const [homeEmail, setHomeEmail] = useState(() => recoveredDraft?.homeEmail ?? '');
  const [homePhone, setHomePhone] = useState(() => recoveredDraft?.homePhone ?? '');
  const [homeAddress, setHomeAddress] = useState(() => recoveredDraft?.homeAddress ?? '');
  const [homeAddressRegion, setHomeAddressRegion] = useState(() => recoveredDraft?.homeAddressRegion ?? '');
  const [homeAddressCity, setHomeAddressCity] = useState(() => recoveredDraft?.homeAddressCity ?? '');
  const [homeAddressPostalCode, setHomeAddressPostalCode] = useState(() => recoveredDraft?.homeAddressPostalCode ?? '');
  const [companyID, setCompanyID] = useState(() => recoveredDraft?.companyID ?? '');
  const [organizationID, setOrganizationID] = useState(() => recoveredDraft?.organizationID ?? '');
  const [locationID, setLocationID] = useState(() => recoveredDraft?.locationID ?? '');
  const [jobProfileID, setJobProfileID] = useState(() => recoveredDraft?.jobProfileID ?? '');
  const [jobLevelID, setJobLevelID] = useState(() => recoveredDraft?.jobLevelID ?? '');
  const [workerNumber, setWorkerNumber] = useState(() => recoveredDraft?.workerNumber ?? '');
  const [businessTitle, setBusinessTitle] = useState(() => recoveredDraft?.businessTitle ?? '');
  const [hireWorkerType, setHireWorkerType] = useState<'EMPLOYEE' | 'CONTINGENT_WORKER'>(() => recoveredDraft?.hireWorkerType ?? 'EMPLOYEE');
  const [hireTimeType, setHireTimeType] = useState<'FULL_TIME' | 'PART_TIME'>(() => recoveredDraft?.hireTimeType ?? 'FULL_TIME');
  const [jobTypeID, setJobTypeID] = useState(() => recoveredDraft?.jobTypeID ?? '');
  const [workSpaceID, setWorkSpaceID] = useState(() => recoveredDraft?.workSpaceID ?? '');
  const [jobTypeReferences, setJobTypeReferences] = useState<HireReference[]>(() => recoveredDraft?.jobTypeReferences ?? []);
  const [workSpaceReferences, setWorkSpaceReferences] = useState<HireReference[]>(() => recoveredDraft?.workSpaceReferences ?? []);
  const [jobTypeReferenceLoading, setJobTypeReferenceLoading] = useState(false);
  const [workSpaceReferenceLoading, setWorkSpaceReferenceLoading] = useState(false);
  const [jobTypeReferenceError, setJobTypeReferenceError] = useState<string>();
  const [workSpaceReferenceError, setWorkSpaceReferenceError] = useState<string>();
  const [externalEmployerName, setExternalEmployerName] = useState(() => recoveredDraft?.externalEmployerName ?? '');
  const [internalSponsorWorkerID, setInternalSponsorWorkerID] = useState(() => recoveredDraft?.internalSponsorWorkerID ?? '');
  const [accessReviewOn, setAccessReviewOn] = useState(() => recoveredDraft?.accessReviewOn ?? '');
  const [hireDate, setHireDate] = useState(() => recoveredDraft?.hireDate ?? today);
  const hireDateValid = /^\d{4}-\d{2}-\d{2}$/.test(hireDate) && !Number.isNaN(Date.parse(hireDate)) && new Date(hireDate).toISOString().slice(0, 10) === hireDate;
  const [hireReferences, setHireReferences] = useState<HireReferences>(() => recoveredDraft?.hireReferences ?? { companies: [], locations: [], jobs: [], levels: [], organizations: [] });
  const [hireReferenceLoading, setHireReferenceLoading] = useState(false);
  const [hireReferenceError, setHireReferenceError] = useState<string>();
  const [referenceRefresh, setReferenceRefresh] = useState(0);
  const [referenceNotice, setReferenceNotice] = useState('');
  const [operationKeys, setOperationKeys] = useState(() => recoveredDraft?.operationKeys ?? ({ person: crypto.randomUUID(), hire: crypto.randomUUID(), submit: crypto.randomUUID() }));
  useEffect(() => {
    if (!recoveryScope) return;
    if (!hasCreateDraft || !access.canCreatePerson) { clearPeopleRegistrationDraft(recoveryScope); return; }
    savePeopleRegistrationDraft(recoveryScope, {
      legalFamilyName, legalGivenName, legalEnglishFamilyName, legalEnglishGivenName, nicknameKorean, nicknameEnglish, systemID, birthDate, homeEmail, homePhone, homeAddress, homeAddressRegion, homeAddressCity, homeAddressPostalCode, companyID, organizationID, locationID, jobProfileID, jobLevelID, workerNumber, businessTitle, jobTypeID, workSpaceID, externalEmployerName, internalSponsorWorkerID, accessReviewOn, createdPersonID, createdHireID, identityExpanded, privateExpanded, assignmentExpanded, retryOnly, hireDate, gender, hireWorkerType, hireTimeType, createStep, hireReferences, jobTypeReferences, workSpaceReferences, operationKeys, createWorkflow
    });
  }, [recoveryScope, hasCreateDraft, access.canCreatePerson, legalFamilyName, legalGivenName, legalEnglishFamilyName, legalEnglishGivenName, nicknameKorean, nicknameEnglish, systemID, birthDate, homeEmail, homePhone, homeAddress, homeAddressRegion, homeAddressCity, homeAddressPostalCode, companyID, organizationID, locationID, jobProfileID, jobLevelID, workerNumber, businessTitle, jobTypeID, workSpaceID, externalEmployerName, internalSponsorWorkerID, accessReviewOn, createdPersonID, createdHireID, identityExpanded, privateExpanded, assignmentExpanded, retryOnly, hireDate, gender, hireWorkerType, hireTimeType, createStep, hireReferences, jobTypeReferences, workSpaceReferences, operationKeys, createWorkflow]);
  const legalFullName = `${legalFamilyName.trim()}${legalGivenName.trim()}`;
  const legalEnglishFullName = [legalEnglishGivenName.trim(), legalEnglishFamilyName.trim()].filter(Boolean).join(' ');
  const genderLabel = gender === 'MALE' ? '남성' : gender === 'FEMALE' ? '여성' : '-';
  const homeAddressLabel = [homeAddress.trim(), homeAddressCity.trim(), homeAddressRegion.trim(), homeAddressPostalCode.trim()].filter(Boolean).join(' ');
  const jobTypeLabel = hireWorkerType === 'EMPLOYEE' ? '고용 유형' : '외부 인력 유형';
  const createReferenceLoading = hireReferenceLoading || jobTypeReferenceLoading || workSpaceReferenceLoading;
  const createReferenceError = hireReferenceError || jobTypeReferenceError || workSpaceReferenceError;
  const hireLocked = retryOnly || Boolean(createdHireID) || createSubmitting;
  const hireOrganizationOptions = hireReferences.organizations.map((item) => ({ ...item, descriptor: organizationPath(item, hireReferences.organizations) }));
  const hireJobProfile = hireReferences.jobs.find((item) => item.id === jobProfileID);
  const hireJobLevel = hireReferences.levels.find((item) => item.id === jobLevelID);
  const businessTitleReferencesReady = !hireReferenceLoading
    && (!jobProfileID || Boolean(hireJobProfile?.descriptor?.trim()))
    && (!jobLevelID || Boolean(hireJobLevel?.descriptor?.trim()));
  const selected = detailWorkerID ? values.find((worker) => worker.id === detailWorkerID) : undefined;
  const {
    canReadNames,
    canReadHomeContact,
    canReadHomeContactFull,
    canReadWorkContact,
    canReadWorkContactFull,
    canReadPersonalData,
    canReadRewards,
    canWritePerson,
    canEditNames,
    canManageProfilePhoto,
    canReadFamily,
    canWriteFamily,
    canApproveFamily,
    canReadCareer,
    canWriteCareer,
    canApproveCareer,
    canCreatePerson,
    canBulkCreate,
    canOpenWorkflowRequests,
  } = access;
  const nameProfileResource = useAsyncResource<{ personId?: string; value?: PersonNameProfile; state: 'ready' | 'forbidden' | 'unavailable' }>(
    async () => {
      const personId = selected?.personId;
      if (!canReadNames) return { personId, state: 'forbidden' };
      if (!personId || !selected) return { personId, state: 'unavailable' };
      if (fixtureMode) return { personId, value: fixtureNameProfiles[personId] ?? fixturePersonNameProfile(selected), state: 'ready' };
      const result = await settleSensitiveResource(profilePort.getPersonNames(personId));
      if (result.error) throw result.error;
      return { personId, value: result.value, state: result.forbidden ? 'forbidden' : result.value ? 'ready' : 'unavailable' };
    },
    [canReadNames, fixtureMode, profilePort, selected?.personId, fixtureNameProfiles[selected?.personId ?? '']],
  );
  const selectedNameProfile = canReadNames && !nameProfileResource.error && nameProfileResource.data?.value?.person.id === selected?.personId
    ? nameProfileResource.data?.value
    : undefined;
  const nameState = !canReadNames ? 'forbidden'
    : nameProfileResource.loading ? 'loading'
      : nameProfileResource.error ? 'error'
        : nameProfileResource.data && nameProfileResource.data.personId === selected?.personId ? nameProfileResource.data.state : 'loading';
  const privateProfileResource = useAsyncResource<WorkerPrivateProfile>(
    async () => {
      if (!selected?.personId) return {
        personId: undefined,
        canReadPersonalData,
        personalDataLoaded: false,
        canReadHomeContact,
        canReadHomeContactFull,
        canReadWorkContact,
        canReadWorkContactFull,
        homeContactLoaded: false,
        workContactLoaded: false,
      };
      if (fixtureMode) return fixtureWorkerPrivateProfile(selected);
      const [homeEmails, homePhones, homeAddresses, emergencyContacts, workEmails, workPhones, personalInformation] = await Promise.all([
        settleSensitiveResourceWhen(canReadHomeContact, () => profilePort.listPersonHomeEmails(selected.personId)),
        settleSensitiveResourceWhen(canReadHomeContact, () => profilePort.listPersonHomePhones(selected.personId)),
        settleSensitiveResourceWhen(canReadHomeContact, () => profilePort.listPersonHomeAddresses(selected.personId)),
        settleSensitiveResourceWhen(canReadHomeContact, () => profilePort.listPersonEmergencyContacts(selected.personId)),
        settleSensitiveResourceWhen(canReadWorkContact, () => profilePort.listPersonWorkEmails(selected.personId)),
        settleSensitiveResourceWhen(canReadWorkContact, () => profilePort.listPersonWorkPhones(selected.personId)),
        settleSensitiveResourceWhen(canReadPersonalData, () => profilePort.getPersonPersonalInformation(selected.personId)),
      ]);
      const resources = [homeEmails, homePhones, homeAddresses, emergencyContacts, workEmails, workPhones, personalInformation];
      // 비상 연락처는 별도 원장이므로 조회 실패가 개인 이메일·전화·주소 수정까지 막지 않게 분리한다.
      const homeContactForbidden = [homeEmails, homePhones, homeAddresses].some((resource) => resource.forbidden);
      const workContactForbidden = [workEmails, workPhones].some((resource) => resource.forbidden);
      return {
        personId: selected.personId,
        birthDate: personalInformation.value?.dateOfBirth,
        gender: personalInformation.value?.gender,
        maritalStatus: personalInformation.value?.maritalStatus,
        homeEmails: homeEmails.value?.data,
        homePhones: homePhones.value?.data,
        homeAddresses: homeAddresses.value?.data,
        emergencyContacts: emergencyContacts.value?.data,
        emergencyContactsLoaded: Boolean(emergencyContacts.value),
        canReadEmergencyContact: canReadHomeContact && !emergencyContacts.forbidden,
        workEmails: workEmails.value?.data,
        workPhones: workPhones.value?.data,
        canReadPersonalData: canReadPersonalData && !personalInformation.forbidden,
        personalDataLoaded: Boolean(personalInformation.value || personalInformation.missing),
        canReadHomeContact: canReadHomeContact && !homeContactForbidden,
        canReadHomeContactFull: canReadHomeContactFull && !homeContactForbidden,
        canReadWorkContact: canReadWorkContact && !workContactForbidden,
        canReadWorkContactFull: canReadWorkContactFull && !workContactForbidden,
        homeContactLoaded: !homeContactForbidden && Boolean(homeEmails.value && homePhones.value && homeAddresses.value),
        workContactLoaded: !workContactForbidden && Boolean(workEmails.value && workPhones.value),
        partialFailure: resources.some((resource) => Boolean(resource.error)),
      };
    },
    [canReadHomeContact, canReadHomeContactFull, canReadPersonalData, canReadWorkContact, canReadWorkContactFull, fixtureMode, profilePort, selected?.personId],
  );
  const loadedPrivateProfile = privateProfileResource.data;
  const selectedPrivateProfile: WorkerPrivateProfile = loadedPrivateProfile && loadedPrivateProfile.personId === selected?.personId ? loadedPrivateProfile : {
    personId: selected?.personId,
    canReadPersonalData,
    personalDataLoaded: false,
    canReadHomeContact,
    canReadHomeContactFull,
    canReadWorkContact,
    canReadWorkContactFull,
    homeContactLoaded: false,
    workContactLoaded: false,
    emergencyContactsLoaded: false,
  };
  const employmentProfileResource = useAsyncResource<WorkerEmploymentProfile>(
    async () => {
      if (!selected) return { canReadContract: canReadRewards };
      if (fixtureMode) return fixtureWorkerEmploymentProfile(selected);
      if (!canReadRewards) return { workerId: selected.id, canReadContract: false };
      const rewards = await settleSensitiveResource(contractApi ? contractApi.getContracts(selected.id, asOf) : profilePort.getWorkerRewards(selected.id, asOf));
      if (rewards.error) throw rewards.error;
      return {
        workerId: selected.id,
        canReadContract: !rewards.forbidden,
        contractType: rewards.value?.contracts.find((contract) => contract.contractStatus === 'ACTIVE')?.contractType,
      };
    },
    [asOf, canReadRewards, fixtureMode, profilePort, contractApi, selected?.id],
  );
  const loadedEmploymentProfile = employmentProfileResource.data;
  const selectedEmploymentProfile: WorkerEmploymentProfile = loadedEmploymentProfile && loadedEmploymentProfile.workerId === selected?.id
    ? loadedEmploymentProfile
    : { workerId: selected?.id, canReadContract: canReadRewards };
  const issues = useMemo(() => collectPeopleIssues(values, canReadWorkContactFull), [values, canReadWorkContactFull]);
  const rows = filterPeople(values, filters, issues, organizationResource.data ?? []);
  const organizationFilterPending = Boolean(organizationFilter && (organizationResource.loading || organizationResource.error));
  const selectedRows = values.filter((worker) => selection.includes(worker.id));
  const visibleRowIds = new Set<GridRowId>(rows.map((worker) => worker.id));
  const visibleSelectedRows = selectedRows.filter((worker) => visibleRowIds.has(worker.id));
  const canSelect = Boolean((access.canBulkChange && bulkPort) || (access.canExport && port.downloadWorkerReport));
  // 표에는 현재 조회의 선택만 전달하고, 조회 밖의 선택은 이 화면이 별도로 보존합니다.
  const changeVisibleSelection = (ids: GridRowId[]) => setSelection((current) => [
    ...current.filter((id) => !visibleRowIds.has(id)),
    ...ids.filter((id) => visibleRowIds.has(id)),
  ]);
  useEffect(() => { setSelection([]); setSelectionOpen(false); }, [asOf]);
  useEffect(() => {
    if (resource.loading || resource.error) return;
    const available = new Set(values.map((worker) => worker.id));
    setSelection((current) => current.every((id) => available.has(String(id))) ? current : current.filter((id) => available.has(String(id))));
  }, [resource.loading, resource.error, values]);
  const downloadRoster = async (scope: 'query' | 'selection') => {
    const workerIds = (scope === 'selection' ? selectedRows : rows).map((worker) => worker.id);
    if (!access.canExport || fixtureMode || !port.downloadWorkerReport || !workerIds.length || resource.loading || resource.error || exporting || (scope === 'query' && organizationFilterPending)) return;
    setExporting(true); setListError('');
    try {
      const blob = await port.downloadWorkerReport(asOf, workerIds);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a'); anchor.href = url; anchor.download = `workers-${scope === 'selection' ? 'selected-' : ''}${asOf}.csv`; anchor.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      notify('명단 파일 다운로드를 시작했습니다.', { tone: 'success' });
    } catch (cause) { setListError(cause instanceof Error ? cause.message : '명단을 내보내지 못했습니다. 다시 시도해 주세요.'); }
    finally { setExporting(false); }
  };
  const collectionState: ResourceCollectionState = resource.loading
    ? { kind: 'loading', label: '구성원을 불러오는 중입니다.' }
    : resource.error
      ? { kind: 'error', message: resource.error.message, onRetry: resource.reload }
      : rows.length === 0
        ? {
            kind: 'empty',
            title: query || statusFilter || organizationFilter || locationFilter || companyFilter || workerTypeFilter || issueFilter ? '조건에 맞는 구성원이 없습니다.' : '등록된 구성원이 없습니다.',
            description: query || statusFilter || organizationFilter || locationFilter || companyFilter || workerTypeFilter || issueFilter ? '검색어나 필터 조건을 변경해 보세요.' : undefined,
            action: query || statusFilter || organizationFilter || locationFilter || companyFilter || workerTypeFilter || issueFilter
              ? <Button variant="weak" onClick={() => { setQuery(''); setStatusFilter(''); setOrganizationFilter(''); setLocationFilter(''); setCompanyFilter(''); setWorkerTypeFilter(''); setIssueFilter(''); }}>검색 조건 초기화</Button>
              : undefined,
          }
        : { kind: 'ready' };
  const columns: GridColDef<WorkerProfileWorker>[] = useMemo(() => [
    { field: 'name', headerName: '이름', width: 180, minWidth: 120, renderCell: ({ row }) => <TableIdentityCell primary={row.name} leading={<WorkerAvatar worker={row} />} /> },
    { field: 'title', headerName: '직무·직함', minWidth: 180, flex: 1 },
    { field: 'email', headerName: '이메일', minWidth: 220, flex: 1.2 },
    { field: 'organization', headerName: '소속 조직', minWidth: 180, flex: 1 },
    { field: 'status', headerName: '상태', width: 140, renderCell: ({ value }) => <StatusBadge {...workerStatusPresentation(value)} /> },
    { field: 'company', headerName: '회사', minWidth: 150, flex: 1 },
    { field: 'jobType', headerName: '고용 유형', width: 130, renderCell: ({ value }) => value ? <ValueBadge label={value} /> : '—' },
    { field: 'readiness', headerName: '확인 필요', width: 170, sortable: false, renderCell: ({ row }) => (issues.get(row.id)?.length ?? 0) > 0 ? <StatusBadge tone="warning" label={(issues.get(row.id) ?? []).map((issue) => issue.label).join(' · ')} /> : <Typography variant="caption" color="text.secondary">—</Typography> },
    { field: 'workerNumber', headerName: '사번', width: 120 },
    { field: 'hireDate', headerName: '최근 입사일', width: 120 },
    { field: 'actions', headerName: '', width: 64, sortable: false, renderCell: ({ row, tabIndex }) => <IconButton size="small" tabIndex={tabIndex} aria-label={`${row.name} 상세 보기`} onClick={(event) => { event.stopPropagation(); route.openWorker(row.id, asOf); }}><ChevronRightOutlined fontSize="small" /></IconButton> },
  ], [issues, route, asOf]);
  const canEditContact = canWritePerson && selectedPrivateProfile.personId === selected?.personId &&
    !privateProfileResource.loading && (
    (selectedPrivateProfile.canReadHomeContactFull && selectedPrivateProfile.homeContactLoaded) ||
    (selectedPrivateProfile.canReadWorkContactFull && selectedPrivateProfile.workContactLoaded)
  );
  const personnelWorker: StaffingWorker | undefined = selected ? { ...selected, profilePhotoUrl: selected.photoUrl,
    organizationId: selected.organizationId || (fixtureMode ? fixturePeopleHireReferences.organizations.find(item => item.descriptor === selected.organization)?.id : undefined),
    additionalOrganizations: selected.additionalJobs.flatMap(job => {
      const id = job.organizationId || (fixtureMode ? fixturePeopleHireReferences.organizations.find(item => item.descriptor === job.organization)?.id : undefined);
      return id ? [{ id, descriptor: job.organization }] : [];
    }),
  } : undefined;
  const originalHire = (staffingHistory.data ?? []).filter(change => change.processType === 'HIRE' && change.status === 'COMPLETED' && !change.correctionOf && change.worker?.id === selected?.id).sort((a, b) => (b.effectiveDate ?? '').localeCompare(a.effectiveDate ?? ''))[0];
  const openPersonnelSession = (operation: { action: WorkerPersonnelAction } | { kind: WorkerTransitionKind }) => {
    setPersonnelAnchor(null);
    if (!personnelWorker) return;
    const id = `${personnelWorker.id}-${'action' in operation ? operation.action : operation.kind}`;
    setPersonnelSessions(sessions => sessions.some(session => session.id === id)
      ? sessions.map(session => ({ ...session, open: session.id === id }))
      : [...sessions.map(session => ({ ...session, open: false })), { id, open: true, worker: personnelWorker, asOf, correction: originalHire, ...operation }]);
  };
  const openPersonnel = (action: WorkerPersonnelAction) => openPersonnelSession({ action });
  const personnelChanged = () => { personnelRefreshPending.current = true; };
  const refreshPersonnel = () => {
    if (!personnelRefreshPending.current) return;
    personnelRefreshPending.current = false;
    resource.reload(); staffingHistory.reload(); employmentProfileResource.reload();
  };
  const closePersonnel = (id: string, retain: boolean) => {
    setPersonnelSessions(sessions => retain ? sessions.map(session => session.id === id ? { ...session, open: false } : session) : sessions.filter(session => session.id !== id));
    refreshPersonnel();
  };
  const startSelectedWorkerStaffing = selected && access.canChangeStaffing && route.openStaffingPlan
    ? () => { setPersonnelAnchor(null); route.openStaffingPlan!({ workerId: selected.id, organizationId: selected.organizationId, effectiveDate: asOf, view: 'general' }); }
    : selected && access.canBulkChange && bulkPort ? () => { setPersonnelAnchor(null); setBulkChangeWorkers([selected]); } : undefined;
  const startSelectedWorkerOrganizationStaffing = selected && access.canChangeStaffing && route.openStaffingPlan
    ? () => { setPersonnelAnchor(null); route.openStaffingPlan!({ workerId: selected.id, organizationId: selected.organizationId, effectiveDate: asOf, view: 'general' }); }
    : undefined;
  useEffect(() => {
    if (!createOpen || hireLocked) return;
    if (!hireDateValid) { setHireReferenceLoading(false); setHireReferenceError(undefined); return; }
    let active = true;
    setHireReferenceLoading(true);
    setHireReferenceError(undefined);
    const request = fixtureMode ? Promise.resolve(fixturePeopleHireReferences) : port.listHireReferences(hireDate);
    void request.then((references) => {
      if (!active) return;
      setHireReferences(references);
      const retain = (id: string, options: readonly { id: string }[]) => options.some((option) => option.id === id) ? id : '';
      setCompanyID((id) => retain(id, references.companies));
      setOrganizationID((id) => retain(id, references.organizations));
      setLocationID((id) => retain(id, references.locations));
      setJobProfileID((id) => retain(id, references.jobs));
      setJobLevelID((id) => retain(id, references.levels));
    }, (failure: unknown) => {
      if (active) setHireReferenceError(failure instanceof Error ? failure.message : '입사 기준정보를 불러오지 못했습니다.');
    }).finally(() => { if (active) setHireReferenceLoading(false); });
    return () => { active = false; };
  }, [port, createOpen, fixtureMode, hireDate, hireDateValid, hireLocked, referenceRefresh]);
  useEffect(() => {
    if (!createOpen || hireLocked) return;
    if (!hireDateValid) { setJobTypeReferenceLoading(false); setJobTypeReferenceError(undefined); return; }
    let active = true;
    setJobTypeReferenceLoading(true);
    setJobTypeReferenceError(undefined);
    const request = fixtureMode
      ? Promise.resolve(fixturePeopleJobTypes[hireWorkerType])
      : port.listHireJobTypes(hireWorkerType, hireDate);
    void request.then((items) => {
      if (!active) return;
      setJobTypeReferences(items);
      setJobTypeID((id) => items.some((item) => item.id === id) ? id : '');
    }, (failure: unknown) => {
      if (active) setJobTypeReferenceError(failure instanceof Error ? failure.message : `${jobTypeLabel} 기준정보를 불러오지 못했습니다.`);
    }).finally(() => { if (active) setJobTypeReferenceLoading(false); });
    return () => { active = false; };
  }, [port, createOpen, fixtureMode, hireDate, hireDateValid, hireWorkerType, jobTypeLabel, hireLocked, referenceRefresh]);
  useEffect(() => {
    if (!createOpen || hireLocked) return;
    if (!hireDateValid) { setWorkSpaceReferenceLoading(false); setWorkSpaceReferenceError(undefined); return; }
    setWorkSpaceReferenceError(undefined);
    if (!locationID) { setWorkSpaceID(''); setWorkSpaceReferences([]); setWorkSpaceReferenceLoading(false); return; }
    let active = true;
    setWorkSpaceReferenceLoading(true);
    const request = fixtureMode
      ? Promise.resolve(fixturePeopleWorkSpaces[locationID] ?? [])
      : port.listHireWorkSpaces(locationID, hireDate);
    void request.then((items) => {
      if (!active) return;
      setWorkSpaceReferences(items);
      setWorkSpaceID((id) => items.some((item) => item.id === id) ? id : '');
    }, (failure: unknown) => {
      if (active) setWorkSpaceReferenceError(failure instanceof Error ? failure.message : '업무 공간 기준정보를 불러오지 못했습니다.');
    }).finally(() => { if (active) setWorkSpaceReferenceLoading(false); });
    return () => { active = false; };
  }, [port, createOpen, fixtureMode, hireDate, hireDateValid, locationID, hireLocked, referenceRefresh]);
  useEffect(() => {
    if (!hasCreateDraft || !(legalFullName || homeEmail || companyID || createdPersonID)) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasCreateDraft, legalFullName, homeEmail, companyID, createdPersonID]);
  useEffect(() => route.subscribe(() => {
    setDetailWorkerID(route.read().workerId);
  }), [route]);
  useEffect(() => {
    setContactOpen(false);
    setContactWorkflows([]);
    setNameOpen(false);
  }, [selected?.personId]);
  const openWorkerDetail = (workerID: string) => {
    route.openWorker(workerID, asOf);
  };
  const changeAsOf = (value: string) => {
    setAsOf(value);
    if (detailWorkerID) route.openWorker(detailWorkerID, value, { replace: true });
  };
  const closeWorkerDetail = () => {
    route.closeWorker();
  };
  const openCreate = () => {
    if (!canCreatePerson) return;
    if (hasCreateDraft) { setCreateOpen(true); return; }
    pendingPersonFieldFocus.current = undefined;
    setHasCreateDraft(true); setRetryOnly(false); setIdentityExpanded(false); setPrivateExpanded(false); setAssignmentExpanded(false); setReferenceNotice('');
    setCreateErrorFocusRequest(0); setHireStepValidated(false);
    setCreateStep(0); setPersonStepValidated(false); setPersonTouchedFields({}); setCreateError(undefined); setCreateWorkflow(undefined); setCreatedPersonID(''); setCreatedHireID('');
    setLegalFamilyName(''); setLegalGivenName(''); setLegalEnglishFamilyName(''); setLegalEnglishGivenName(''); setNicknameKorean(''); setNicknameEnglish(''); setSystemID(''); setBirthDate(''); setGender(''); setHomeEmail(''); setHomePhone(''); setHomeAddress(''); setHomeAddressRegion(''); setHomeAddressCity(''); setHomeAddressPostalCode(''); setCompanyID(''); setOrganizationID(''); setLocationID(''); setJobProfileID(''); setJobLevelID(''); setJobTypeID(''); setWorkSpaceID(''); setWorkerNumber(''); setBusinessTitle(''); setHireWorkerType('EMPLOYEE'); setHireTimeType('FULL_TIME'); setExternalEmployerName(''); setInternalSponsorWorkerID(''); setAccessReviewOn(''); setHireDate(today);
    setOperationKeys({ person: crypto.randomUUID(), hire: crypto.randomUUID(), submit: crypto.randomUUID() });
    setCreateOpen(true);
  };
  // 검색 등 외부 진입은 기존 폼을 열고 일회성 action만 소비합니다. 초안은 그대로 이어갑니다.
  useEffect(() => {
    const openFromRoute = () => {
      if (route.read().action !== 'create' || !route.clearCreateAction) return;
      route.clearCreateAction();
      openCreate();
    };
    openFromRoute();
    return route.subscribe(openFromRoute);
  }, [route, openCreate]);
  const discardCreateDraft = async () => {
    if (createdPersonID || createdHireID || retryOnly || createPending.current) return;
    if (await confirm({ title: '작성 내용을 버릴까요?', description: '아직 제출하지 않은 구성원 등록 내용이 삭제됩니다.', confirmLabel: '작성 내용 버리기', cancelLabel: '계속 작성', intent: 'discard' })) {
      clearPeopleRegistrationDraft(recoveryScope); setHasCreateDraft(false); setCreateOpen(false);
    }
  };
  const personStepErrors = {
    legalFamilyName: legalFamilyName.trim() ? undefined : '법적 성을 입력해 주세요.',
    legalGivenName: legalGivenName.trim() ? undefined : '법적 이름을 입력해 주세요.',
    legalEnglishFamilyName: legalEnglishGivenName.trim() && !legalEnglishFamilyName.trim() ? '영문 법적 성을 함께 입력해 주세요.' : undefined,
    legalEnglishGivenName: legalEnglishFamilyName.trim() && !legalEnglishGivenName.trim() ? '영문 법적 이름을 함께 입력해 주세요.' : undefined,
    nicknameKorean: nicknameKorean.trim().length > 200 ? '닉네임은 200자 이내로 입력해 주세요.' : undefined,
    nicknameEnglish: nicknameEnglish.trim().length > 200 ? '닉네임은 200자 이내로 입력해 주세요.' : undefined,
    systemID: !systemID.trim() ? undefined
      : /^[a-z0-9][a-z0-9._-]{0,20}$/.test(systemID.trim()) ? undefined
        : '영문 소문자 또는 숫자로 시작하고, 소문자·숫자·점·밑줄·하이픈으로 21자 이내로 입력해 주세요.',
    birthDate: (() => {
      if (!birthDate) return undefined;
      const parsed = new Date(`${birthDate}T00:00:00Z`);
      if (Number.isNaN(parsed.valueOf()) || parsed.toISOString().slice(0, 10) !== birthDate) return '생년월일에 실제 날짜를 입력해 주세요.';
      return birthDate > today ? '생년월일은 오늘 이후일 수 없습니다.' : undefined;
    })(),
    homeEmail: homeEmail.trim() && (homeEmail.trim().length > 320 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(homeEmail.trim()))
      ? '개인 이메일 형식을 확인해 주세요.' : undefined,
    homePhone: homePhone.trim() && (!/^[0-9+(). -]{4,50}$/.test(homePhone.trim()) || !/\d/.test(homePhone.trim()))
      ? '휴대폰 연락처는 숫자를 하나 이상 포함해야 하며 + ( ) . - 공백만 사용할 수 있습니다.' : undefined,
    homeAddress: !homeAddress.trim() && (homeAddressRegion.trim() || homeAddressCity.trim() || homeAddressPostalCode.trim())
      ? '시·도, 시·군·구 또는 우편번호를 입력하려면 집 주소도 입력해 주세요.' : undefined,
    homeAddressPostalCode: homeAddressPostalCode.trim() && !/^\d{5}$/.test(homeAddressPostalCode.trim())
      ? '우편번호는 숫자 5자리로 입력해 주세요.' : undefined,
  };
  const personFieldFeedback = (field: keyof typeof personStepErrors) => {
    const message = personStepValidated || personTouchedFields[field] ? personStepErrors[field] : undefined;
    return {
      id: createFieldId(field),
      error: Boolean(message),
      helperText: message,
      onBlur: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        // 자동 초점 이동만으로 빈 필수 입력을 오류 상태로 만들지 않습니다.
        if (event.target.value.trim()) setPersonTouchedFields((current) => ({ ...current, [field]: true }));
      },
    };
  };
  const personFieldLabels: Record<keyof typeof personStepErrors, string> = {
    legalFamilyName: '법적 이름 · 성', legalGivenName: '법적 이름 · 이름',
    legalEnglishFamilyName: '법적 이름(영문) · 성', legalEnglishGivenName: '법적 이름(영문) · 이름',
    nicknameKorean: '사내에서 사용하는 닉네임(한글)', nicknameEnglish: '사내에서 사용하는 닉네임(영문)',
    systemID: '아이디 (이메일 ID)', birthDate: '생년월일', homeEmail: '개인 이메일',
    homePhone: '휴대폰 연락처', homeAddress: '집 주소', homeAddressPostalCode: '우편번호',
  };
  const hireStepErrors = {
    companyID: companyID ? undefined : '회사를 선택해 주세요.',
    organizationID: organizationID ? undefined : '조직을 선택해 주세요.',
    locationID: locationID ? undefined : '근무지를 선택해 주세요.',
    jobProfileID: jobProfileID ? undefined : '직무를 선택해 주세요.',
    jobTypeID: jobTypeID ? undefined : `${jobTypeLabel}을 선택해 주세요.`,
    hireDate: hireDateValid ? undefined : '입사일에 실제 날짜를 입력해 주세요.',
    workerNumber: hireWorkerType === 'CONTINGENT_WORKER' && !workerNumber.trim() ? '외부 인력 번호를 입력해 주세요.' : undefined,
    externalEmployerName: hireWorkerType !== 'CONTINGENT_WORKER' ? undefined : !externalEmployerName.trim() ? '외부 소속사를 입력해 주세요.' : new TextEncoder().encode(externalEmployerName.trim()).length > 500 ? '외부 소속사는 500바이트 이내로 입력해 주세요.' : undefined,
    internalSponsorWorkerID: hireWorkerType === 'CONTINGENT_WORKER' && !internalSponsorWorkerID ? '내부 스폰서를 선택해 주세요.' : undefined,
    accessReviewOn: hireWorkerType === 'CONTINGENT_WORKER' && !accessReviewOn ? '접근 권한 검토일을 입력해 주세요.' : undefined,
  };
  const hireFieldLabels: Record<keyof typeof hireStepErrors, string> = {
    companyID: '회사', organizationID: '조직', locationID: '근무지', jobProfileID: '직무', jobTypeID: jobTypeLabel,
    hireDate: '입사일', workerNumber: '외부 인력 번호', externalEmployerName: '외부 소속사',
    internalSponsorWorkerID: '내부 스폰서', accessReviewOn: '접근 권한 검토일',
  };
  const hireFieldFeedback = (field: keyof typeof hireStepErrors) => ({
    id: createFieldId(field),
    error: hireStepValidated && Boolean(hireStepErrors[field]),
    helperText: hireStepValidated ? hireStepErrors[field] : undefined,
  });
  const focusPersonField = (field: string) => {
    const target = document.getElementById(createFieldId(field));
    target?.focus();
    target?.scrollIntoView({ block: 'nearest' });
  };
  const openPersonField = (field: string) => {
    const section = ['legalEnglishFamilyName', 'legalEnglishGivenName', 'nicknameKorean', 'nicknameEnglish', 'systemID'].includes(field) ? 'identity'
      : ['birthDate', 'homeAddress', 'homeAddressPostalCode'].includes(field) ? 'private' : undefined;
    if (!section || (section === 'identity' ? identityExpanded : privateExpanded)) {
      focusPersonField(field);
      return;
    }
    pendingPersonFieldFocus.current = { field, section };
    if (section === 'identity') setIdentityExpanded(true);
    else setPrivateExpanded(true);
  };
  const focusExpandedPersonField = (section: 'identity' | 'private') => {
    const pending = pendingPersonFieldFocus.current;
    if (pending?.section !== section) return;
    pendingPersonFieldFocus.current = undefined;
    focusPersonField(pending.field);
  };
  const createSummaryItems: FormErrorSummaryItem[] = createStep === 0 && personStepValidated
    ? (Object.keys(personStepErrors) as Array<keyof typeof personStepErrors>).flatMap((field) => personStepErrors[field]
      ? [{ id: field, label: personFieldLabels[field], message: personStepErrors[field], fieldId: createFieldId(field), onActivate: () => openPersonField(field) }] : [])
    : createStep === 1 && hireStepValidated
      ? (Object.keys(hireStepErrors) as Array<keyof typeof hireStepErrors>).flatMap((field) => hireStepErrors[field]
        ? [{ id: field, label: hireFieldLabels[field], message: hireStepErrors[field], fieldId: createFieldId(field) }] : [])
      : [];
  const validatePersonStep = () => {
    const errors = Object.entries(personStepErrors).filter(([, error]) => error);
    if (errors.some(([field]) => ['legalEnglishFamilyName', 'legalEnglishGivenName', 'nicknameKorean', 'nicknameEnglish', 'systemID'].includes(field))) setIdentityExpanded(true);
    if (errors.some(([field]) => ['birthDate', 'homeAddress', 'homeAddressPostalCode'].includes(field))) setPrivateExpanded(true);
    return errors[0]?.[1];
  };
  const nextCreateStep = () => {
    if (createSubmitting || hireLocked) return;
    const invalid = createStep === 0 ? (setPersonStepValidated(true), validatePersonStep())
      : (setHireStepValidated(true), Object.values(hireStepErrors).some(Boolean));
    if (invalid) { setCreateError(undefined); setCreateErrorFocusRequest((current) => current + 1); return; }
    if (createStep === 1 && (createReferenceLoading || createReferenceError)) return;
    setCreateError(undefined); setCreateStep(createStep + 1);
  };
  const submitHire = () => {
    if (!canCreatePerson || createPending.current || (!retryOnly && (createReferenceLoading || createReferenceError))) return;
    setPersonStepValidated(true);
    const personStepError = validatePersonStep();
    if (personStepError) {
      setCreateError(undefined); setCreateStep(0); setCreateErrorFocusRequest((current) => current + 1); return;
    }
    setHireStepValidated(true);
    if (Object.values(hireStepErrors).some(Boolean)) {
      setCreateError(undefined); setCreateStep(1); setCreateErrorFocusRequest((current) => current + 1); return;
    }
    createPending.current = true;
    let stage: 'person' | 'hire' | 'submit' = createdPersonID ? 'hire' : 'person';
    setCreateSubmitting(true); setRetryOnly(true); setCreateError(undefined);
    const createPerson = createdPersonID
      ? Promise.resolve({ id: createdPersonID })
      : fixtureMode
        ? Promise.resolve({ id: 'person-new-fixture' })
        : port.createPerson({
          legalName: { fullName: legalFullName, firstName: legalGivenName.trim(), lastName: legalFamilyName.trim(), countryCode: 'KR', locale: 'ko-KR' },
          legalEnglishName: legalEnglishFullName ? { fullName: legalEnglishFullName, firstName: legalEnglishGivenName.trim(), lastName: legalEnglishFamilyName.trim(), countryCode: 'US', locale: 'en-US' } : undefined,
          nickname: nicknameKorean.trim() ? { fullName: nicknameKorean.trim(), countryCode: 'KR', locale: 'ko-KR' } : undefined,
          nicknameEnglish: nicknameEnglish.trim() ? { fullName: nicknameEnglish.trim(), countryCode: 'US', locale: 'en-US' } : undefined,
          systemId: systemID.trim() || undefined,
          birthDate: birthDate || undefined,
          gender: gender || undefined,
          homeEmail: homeEmail.trim() || undefined,
          homePhone: homePhone.trim() || undefined,
          homeAddress: homeAddress.trim() ? {
            countryCode: 'KR',
            region: homeAddressRegion.trim() || undefined,
            city: homeAddressCity.trim() || undefined,
            postalCode: homeAddressPostalCode.trim() || undefined,
            addressLines: [homeAddress.trim()],
          } : undefined,
        }, operationKeys.person);
    void createPerson.then(async (created): Promise<WorkdayReference | undefined> => {
      setCreatedPersonID(created.id);
      let hireID = createdHireID;
      let draftWorkflow = createWorkflow;
      if (!hireID) {
        stage = 'hire';
        const selectedSponsor = values.find((worker) => worker.id === internalSponsorWorkerID);
        const input = {
          effectiveDate: hireDate,
          company: selectedHireReference(companyID, hireReferences.companies),
          workerType: hireWorkerType,
          workerNumber: hireWorkerType === 'CONTINGENT_WORKER' ? workerNumber.trim() : undefined,
          supervisoryOrganization: selectedHireReference(organizationID, hireReferences.organizations),
          jobProfile: selectedHireReference(jobProfileID, hireReferences.jobs),
          jobLevel: selectedHireReference(jobLevelID, hireReferences.levels),
          jobType: selectedHireReference(jobTypeID, jobTypeReferences),
          location: selectedHireReference(locationID, hireReferences.locations),
          workSpace: selectedHireReference(workSpaceID, workSpaceReferences),
          businessTitle: businessTitle.trim() || undefined,
          timeType: hireTimeType,
          externalEmployerName: hireWorkerType === 'CONTINGENT_WORKER' ? externalEmployerName.trim() : undefined,
          internalSponsorWorker: hireWorkerType === 'CONTINGENT_WORKER'
            ? { id: internalSponsorWorkerID, descriptor: selectedSponsor?.name }
            : undefined,
          accessReviewOn: hireWorkerType === 'CONTINGENT_WORKER' ? accessReviewOn : undefined,
        };
        if (fixtureMode) {
          hireID = 'hire-new-fixture';
        } else {
          const hire = await port.createHire(created.id, input, operationKeys.hire);
          hireID = hire.id;
          draftWorkflow = hire.workflow;
        }
        setCreatedHireID(hireID);
        if (draftWorkflow) setCreateWorkflow(draftWorkflow);
      }
      if (fixtureMode) return draftWorkflow;
      stage = 'submit';
      const submitted = await port.submitHire(hireID, operationKeys.submit);
      return submitted.workflow ?? draftWorkflow;
    }).then((workflow) => {
      setSubmittedSummary({ name: formatPersonDisplayName(nicknameKorean, legalFullName), hireDate, organization: hireOrganizationOptions.find((item) => item.id === organizationID)?.descriptor, workerNumber: hireWorkerType === 'CONTINGENT_WORKER' ? workerNumber : undefined });
      clearPeopleRegistrationDraft(recoveryScope); setHasCreateDraft(false); setRetryOnly(false);
      setCreateOpen(false); setCreatedPersonID(''); setCreatedHireID(''); setCreateNotice(`${formatPersonDisplayName(nicknameKorean, legalFullName)}님의 입사 승인 요청을 제출했습니다.`); setSubmittedWorkflow(workflow); setCreateWorkflow(undefined); resource.reload();
    }).catch((failure: unknown) => {
      const editable = stage !== 'submit' && httpStatus(failure) === 400 && failure != null && typeof failure === 'object' && 'code' in failure && failure.code === 'INVALID_REQUEST';
      setRetryOnly(!editable);
      if (editable) {
        setOperationKeys((keys) => ({ person: stage === 'person' ? crypto.randomUUID() : keys.person, hire: crypto.randomUUID(), submit: crypto.randomUUID() }));
        setCreateStep(stage === 'person' ? 0 : 1);
      }
      setCreateError((failure instanceof Error ? failure.message : '구성원 등록을 완료하지 못했습니다.') + (editable
        ? ' 입력 내용을 수정한 뒤 다시 제출해 주세요.'
        : ' 처리 결과를 확인할 수 없어 입력 내용을 유지했습니다. 같은 요청으로 다시 시도해 주세요.'));
    }).finally(() => { createPending.current = false; setCreateSubmitting(false); });
  };
  useEffect(() => {
    if (route.read().action !== 'contracts' || !selected || !canReadRewards || !contractApi) return;
    setContractOpen(true); route.clearCreateAction?.();
  }, [route, selected, canReadRewards, contractApi]);
  const openContactChange = (section?: 'HOME' | 'WORK' | 'EMERGENCY') => {
    if (!canEditContact || !selected) return;
    setContactSection(section);
    setContactNotice(undefined); setContactWorkflows([]); setContactOpen(true);
  };
  const openNameChange = () => {
    if (!canEditNames || !selected?.personId) return;
    setNameOpen(true);
  };
  const closeContactNotice = () => {
    setContactNotice(undefined);
    setContactWorkflows([]);
  };
  const openWorkflowRequest = (workflow: WorkdayReference) => {
    if (!canOpenWorkflowRequests) return;
    route.openWorkflowRequest(workflow.id);
  };
  const closeCreateNotice = () => {
    setCreateNotice(undefined);
    setSubmittedWorkflow(undefined);
  };
  const contactWorkflowAction = canOpenWorkflowRequests && contactWorkflows.length ? <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
    {contactWorkflows.map(({ scope, workflow }) => <Button
      key={`${scope}-${workflow.id}`}
      color="inherit"
      size="small"
      onClick={() => openWorkflowRequest(workflow)}
    >{scope === 'HOME' ? '개인 연락처 문서 열기' : '회사 연락처 문서 열기'}</Button>)}
  </Stack> : undefined;
  return (
    <>
      {detailWorkerID ? <InformationPage
        title="구성원 정보"
        actions={selected && !['퇴직', '종료'].includes(selected.status) && access.canStartOnboarding && route.openOnboarding ? <Button variant="weak" onClick={() => route.openOnboarding?.(selected.id, selected.hireDate)}>온보딩 시작</Button> : undefined}
        width="standard"
        navigation={<IconButton aria-label="구성원 목록으로 돌아가기" onClick={closeWorkerDetail}><ArrowBackOutlined /></IconButton>}
        utilities={<CompactDateField label="기준일" value={asOf} onChange={changeAsOf} />}
      >
        {selected && !resource.loading && (issues.get(selected.id)?.length ?? 0) > 0 && <Alert severity="warning" sx={{ mb: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>확인 필요</Typography>
          {(issues.get(selected.id) ?? []).map((issue) => <Typography key={issue.code} variant="body2">{issue.description}</Typography>)}
          <Stack direction="row" spacing={1}>
            {canEditContact && (issues.get(selected.id) ?? []).some((issue) => ['email', 'duplicateEmail'].includes(issue.code)) && <Button size="small" onClick={() => openContactChange()}>연락처 수정</Button>}
            {startSelectedWorkerStaffing && (issues.get(selected.id) ?? []).some((issue) => !['email', 'duplicateEmail'].includes(issue.code)) && <Button size="small" onClick={startSelectedWorkerStaffing}>인사 발령</Button>}
          </Stack>
        </Alert>}
        {contactNotice && <Box sx={{ mb: 2 }}><ResultState tone="success" title="연락처 변경 요청 완료" description={contactNotice} action={<Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', justifyContent: 'center' }}>{contactWorkflowAction}<Button color="inherit" onClick={closeContactNotice}>닫기</Button></Stack>} /></Box>}
        {nameProfileResource.error && <Alert
          severity="warning"
          action={<Button color="inherit" size="small" onClick={nameProfileResource.reload}>다시 시도</Button>}
          sx={{ mb: 1.5 }}
        >이름 원장을 불러오지 못해 기본 이름만 표시합니다.</Alert>}
        {(privateProfileResource.error || selectedPrivateProfile.partialFailure) && <Alert
          severity="warning"
          action={<Button color="inherit" size="small" onClick={privateProfileResource.reload}>다시 시도</Button>}
          sx={{ mb: 1.5 }}
        >개인정보와 연락처를 불러오지 못해 일부 값을 표시하지 않습니다.</Alert>}
        {staffingHistory.error && <Alert
          severity="warning"
          action={<Button color="inherit" size="small" onClick={staffingHistory.reload}>다시 시도</Button>}
          sx={{ mb: 1.5 }}
        >발령 이력을 확인하지 못했습니다. 다시 조회한 뒤 입사일을 정정할 수 있습니다.</Alert>}
        {employmentProfileResource.error && <Alert
          severity="warning"
          action={<Button color="inherit" size="small" onClick={employmentProfileResource.reload}>다시 시도</Button>}
          sx={{ mb: 1.5 }}
        >근로계약 정보를 불러오지 못해 계약 유형을 표시하지 않습니다.</Alert>}
        <ResourceState
          loading={resource.loading}
          loadingVariant="profile"
          error={resource.error?.message}
          empty={!resource.loading && !resource.error && !selected}
          onRetry={resource.reload}
          emptyTitle="구성원을 찾을 수 없습니다."
          emptyDescription="구성원 목록에서 다시 선택해 주세요."
          emptyAction={<Button variant="outlined" onClick={closeWorkerDetail}>구성원 목록</Button>}
        >
          {selected ? <WorkerDetail
              key={selected.personId}
              worker={selected}
              asOf={asOf}
              nameProfile={selectedNameProfile}
              nameState={nameState}
              privateProfile={selectedPrivateProfile}
              privateLoading={privateProfileResource.loading}
              employmentProfile={selectedEmploymentProfile}
              employmentLoading={employmentProfileResource.loading}
              employmentError={Boolean(employmentProfileResource.error)}
              changes={staffingHistory.data ?? []}
              profilePort={profilePort}
              fixtureMode={fixtureMode}
              canManagePhoto={canManageProfilePhoto}
              canReadFamily={canReadFamily}
              canWriteFamily={canWriteFamily}
              canApproveFamily={canApproveFamily}
              canReadCareer={canReadCareer}
              canWriteCareer={canWriteCareer}
              canApproveCareer={canApproveCareer}
              onEditName={canEditNames ? openNameChange : undefined}
              onEditContact={canEditContact ? openContactChange : undefined}
              onStartStaffing={startSelectedWorkerStaffing}
              onOpenOrganizationStaffing={startSelectedWorkerOrganizationStaffing}
              personnelActions={(startSelectedWorkerStaffing || (access.canChangeStaffing && staffingPort) || (canReadRewards && contractApi)) ? <>
                <Button variant="outlined" size="small" endIcon={<ExpandMoreRounded />} aria-haspopup="menu" aria-expanded={Boolean(personnelAnchor)} aria-controls={personnelAnchor ? personnelMenuId : undefined} onClick={event => setPersonnelAnchor(event.currentTarget)}>인사 변경</Button>
                <AnchoredMenu id={personnelMenuId} anchorEl={personnelAnchor} onClose={() => setPersonnelAnchor(null)} ariaLabel="인사 변경">
                  {startSelectedWorkerStaffing && <MenuItem onClick={startSelectedWorkerStaffing}>인사 발령</MenuItem>}
                  {startSelectedWorkerOrganizationStaffing && <MenuItem onClick={startSelectedWorkerOrganizationStaffing}>조직 발령</MenuItem>}
                  {canReadRewards && contractApi && <MenuItem onClick={() => { setPersonnelAnchor(null); setContractOpen(true); }}>근로계약 변경</MenuItem>}
                  {access.canChangeStaffing && staffingPort && [
                    <Divider key="lifecycle-divider" />,
                    <ListSubheader key="lifecycle-heading" disableSticky>재직 상태</ListSubheader>,
                    ...([['LEAVE_OF_ABSENCE', '휴직'], ['RETURN_FROM_LEAVE', '복직'], ['END_WORKER', '퇴직·계약 종료']] as const).map(([action, label]) => <MenuItem key={action} aria-label={label} onClick={() => openPersonnel(action)}><ListItemText primary={label} secondary={personnelActionMatchesStatus(action, selected.status) ? undefined : `${asOf} 기준 ${personnelStatusLabel(selected.status)} · 다른 날짜 확인`} /></MenuItem>),
                    <Divider key="relationship-divider" />,
                    <ListSubheader key="relationship-heading" disableSticky>고용 관계</ListSubheader>,
                    <MenuItem key="transfer" onClick={() => openPersonnelSession({ kind: 'COMPANY_TRANSFER' })}>회사 이동</MenuItem>,
                    <MenuItem key="type" onClick={() => openPersonnelSession({ kind: 'WORKER_TYPE_CONVERSION' })}>구성원 유형 전환</MenuItem>,
                    <Divider key="correction-divider" />,
                    <MenuItem key="correction" disabled={staffingHistory.loading || Boolean(staffingHistory.error) || !originalHire} onClick={() => openPersonnel('CORRECTION')}>입사일 정정{staffingHistory.loading ? ' · 이력 확인 중' : staffingHistory.error ? ' · 이력 조회 실패' : !originalHire ? ' · 원본 입사 발령 없음' : ''}</MenuItem>,
                  ]}
                </AnchoredMenu>
              </> : undefined}
              onPhotoChanged={() => { if (!fixtureMode) resource.reload(); }}
            /> : null}
        </ResourceState>
      </InformationPage> : <ResourcePage
        title="구성원"
        width="fluid"
        help="선택한 기준일에 조회 권한이 있는 구성원을 표시합니다. ‘확인 필요’ 필터에서 소속·사번·고용 유형·업무 이메일 누락과 중복 의심 항목을 확인할 수 있습니다. 업무 이메일은 원문 조회 권한이 있을 때만 확인합니다."
        utilities={<CompactDateField label="기준일" value={asOf} onChange={changeAsOf} />}
        primaryActionVisibility="always"
        primaryAction={canCreatePerson ? <Button variant="fill" startIcon={<AddOutlined />} endIcon={<ExpandMoreRounded />}
          aria-haspopup="menu" aria-controls={registrationAnchor ? registrationMenuId : undefined} aria-expanded={Boolean(registrationAnchor)}
          onClick={(event) => setRegistrationAnchor(event.currentTarget)}>구성원 등록</Button> : undefined}
        actionItems={preferences ? [{ id: 'views', label: '검색·필터 저장', onClick: () => setSavedViewsOpen(true) }] : []}
        actions={onOpenBulkHistory || (access.canExport && port.downloadWorkerReport) ? <>
          {onOpenBulkHistory && <Button onClick={onOpenBulkHistory}>처리 내역</Button>}
          {access.canExport && port.downloadWorkerReport && <>
            <Button startIcon={<DownloadOutlined />} disabled={exporting || !rows.length || resource.loading || Boolean(resource.error) || organizationFilterPending || fixtureMode} onClick={() => void downloadRoster('query')}>{exporting ? '내보내는 중' : `검색 결과 ${rows.length}명 다운로드`}</Button>
            {fixtureMode && <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, pb: 1 }}>미리보기에서는 다운로드할 수 없습니다.</Typography>}
          </>}
        </> : undefined}
      >
        <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {listError && <Alert severity="error" onClose={() => setListError('')} sx={{ mb: 1 }}>{listError}</Alert>}
          {(createNotice || contactNotice) && <Stack spacing={1} sx={{ mb: 1.5, flexShrink: 0 }}>
            {createNotice && submittedSummary && <PeopleRegistrationOutcome {...submittedSummary} message={createNotice} onOpenRequest={submittedWorkflow && canOpenWorkflowRequests ? () => openWorkflowRequest(submittedWorkflow) : undefined} onClose={closeCreateNotice} />}
            {contactNotice && <ResultState tone="success" title="연락처 변경 요청 완료" description={contactNotice} action={<Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', justifyContent: 'center' }}>{contactWorkflowAction}<Button color="inherit" onClick={closeContactNotice}>닫기</Button></Stack>} />}
          </Stack>}
          {canSelect && selectedRows.length > 0 ? <Stack
            role="group" aria-label="선택 구성원 작업" direction="row" spacing={1}
            sx={{ px: 1.5, py: 1, mb: 1, flexShrink: 0, flexWrap: 'wrap', alignItems: 'center', gap: 1, bgcolor: designTokens.color.actionSoft, borderRadius: designTokens.radius.control }}
          >
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flex: 1 }}>
              <Button variant="outlined" color="inherit" onClick={() => setSelectionOpen(true)}>선택한 {selectedRows.length}명</Button>
              <Typography role="status" variant="body2" color="text.secondary">검색 내 {visibleSelectedRows.length}명</Typography>
            </Stack>
            <Button color="inherit" onClick={clearSelection}>선택 해제</Button>
            {access.canExport && port.downloadWorkerReport && <Button startIcon={<DownloadOutlined />} disabled={exporting || resource.loading || Boolean(resource.error) || fixtureMode} onClick={() => void downloadRoster('selection')}>선택한 {selectedRows.length}명 다운로드</Button>}
            {access.canBulkChange && bulkPort && <Button variant="fill" disabled={resource.loading || Boolean(resource.error)} onClick={() => setBulkChangeWorkers(selectedRows)}>선택 구성원 발령 ({selectedRows.length}명)</Button>}
            {access.canExport && port.downloadWorkerReport && fixtureMode && <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>미리보기에서는 다운로드할 수 없습니다.</Typography>}
          </Stack> : null}
          {organizationFilter && <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
            <ChoiceControl kind="checkbox" label="하위 조직 포함" checked={organizationScope !== 'direct'} disabled={organizationResource.loading || Boolean(organizationResource.error)} onCheckedChange={checked => setOrganizationScope(checked ? 'descendants' : 'direct')} />
            <Typography variant="caption" color="text.secondary">주 소속과 겸직을 함께 조회합니다.</Typography>
          </Stack>}
          {organizationFilter && organizationResource.error && <Alert severity="warning" action={<Button onClick={organizationResource.reload}>다시 시도</Button>}>하위 조직을 확인하지 못했습니다. 조회 결과를 사용하기 전에 다시 시도해 주세요.</Alert>}
          <Box ref={tableContainerRef} sx={{ flex: 1, minHeight: 0 }}>
            <ResourceCollection
              state={collectionState}
              rows={rows}
              totalCount={values.length}
              columns={columns}
              query={query}
              onQueryChange={setQuery}
              searchPlaceholder="이름·사번·조직 검색"
              searchLabel="구성원 이름·사번·조직 검색"
              ariaLabel="구성원 목록"
              selection={canSelect ? { ids: visibleSelectedRows.map((row) => row.id), onChange: changeVisibleSelection } : undefined}
              filters={[
                { label: '회사', value: companyFilter, onChange: setCompanyFilter, options: [{ label: '전체', value: '' }, ...Array.from(new Map(values.filter((worker) => worker.companyId || worker.company).map((worker) => [worker.companyId || worker.company!, { value: worker.companyId || worker.company!, label: worker.company || '회사' }])).values())] },
                { label: '고용 유형', value: workerTypeFilter, onChange: setWorkerTypeFilter, options: [{ label: '전체', value: '' }, ...Array.from(new Map(values.filter((worker) => worker.jobTypeId || worker.jobType).map((worker) => [worker.jobTypeId || worker.jobType!, { value: worker.jobTypeId || worker.jobType!, label: worker.jobType || '고용 유형' }])).values())] },
                { label: '확인 필요', value: issueFilter, onChange: setIssueFilter, options: [{ label: '전체', value: '' }, ...peopleIssueOptions.filter((option) => canReadWorkContactFull || !['email', 'duplicateEmail'].includes(option.value))] },
                { label: '재직 상태', value: statusFilter, onChange: setStatusFilter, options: [{ label: '전체', value: '' }, ...(['등록 중', '입사 예정', '재직', '휴직', '퇴직 예정', '종료', '상태 확인 필요'] as const).map((value) => ({ label: value, value }))] },
                { label: '조직', value: organizationFilter, onChange: setOrganizationFilter, options: [{ label: '전체', value: '' }, ...organizationOptions] },
                { label: '근무지', value: locationFilter, onChange: setLocationFilter, options: [{ label: '전체', value: '' }, ...Array.from(new Set(values.map((worker) => worker.location))).map((value) => ({ label: value, value }))] },
              ]}
              onRowClick={({ id }) => openWorkerDetail(String(id))}
            />
          </Box>
        </Box>
      </ResourcePage>}
      {canCreatePerson && <AnchoredMenu id={registrationMenuId} anchorEl={registrationAnchor} onClose={() => setRegistrationAnchor(null)} placement="bottom-end" ariaLabel="구성원 등록 방식">
        <MenuItem onClick={() => { setRegistrationAnchor(null); openCreate(); }}>{hasCreateDraft ? '작성 중인 등록 이어서' : '개별 등록'}</MenuItem>
        {canBulkCreate && <MenuItem onClick={() => { setRegistrationAnchor(null); route.openBulkHire(); }}>대량 등록</MenuItem>}
        {canBulkCreate && bulkPort && <MenuItem disabled={resource.loading || Boolean(resource.error)} onClick={() => { setRegistrationAnchor(null); setImportOpen(true); }}>기존 재직자 이관</MenuItem>}
      </AnchoredMenu>}
      {selectionOpen && canSelect && <DialogSurface open title="선택 구성원" closeLabel="선택 구성원 닫기" size="standard" onClose={() => { setSelectionOpen(false); if (!selectedRows.length) clearSelection(); }}
        actions={<Button color="inherit" onClick={() => { setSelectionOpen(false); if (!selectedRows.length) clearSelection(); }}>닫기</Button>}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>선택한 {selectedRows.length}명 · 검색 내 {visibleSelectedRows.length}명</Typography>
        {selectedRows.length ? <Stack component="ul" spacing={0} sx={{ m: 0, p: 0, listStyle: 'none' }}>
          {selectedRows.map((worker) => <Stack component="li" key={worker.id} direction="row" spacing={1.5} sx={{ alignItems: 'center', py: 1.5, '& + &': { borderTop: 1, borderColor: 'divider' } }}>
            <WorkerAvatar worker={worker} />
            <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{worker.name}</Typography>
              <Typography variant="caption" color="text.secondary">{[worker.title, worker.organization].filter(Boolean).join(' · ')}</Typography>
              {!visibleRowIds.has(worker.id) && <Typography variant="caption" color="text.secondary">현재 검색 결과 밖</Typography>}
            </Stack>
            <Button color="inherit" size="small" aria-label={`${worker.name} 선택 해제`} onClick={() => setSelection((current) => current.filter((id) => id !== worker.id))}>선택 해제</Button>
          </Stack>)}
        </Stack> : <Typography variant="body2">선택한 구성원이 없습니다.</Typography>}
      </DialogSurface>}
      {savedViewsOpen && preferences && <PeopleSavedViews preferences={preferences} filters={filters} onApply={applyFilters} onClose={() => setSavedViewsOpen(false)} onChanged={(message) => notify(message, { tone: 'success' })} />}
      {importOpen && bulkPort && <PeopleImportDialog port={port} bulkPort={bulkPort} workers={values} asOf={asOf} fixtureMode={fixtureMode} onClose={() => setImportOpen(false)} onOpenJob={(id) => { setImportOpen(false); route.openBulkJob?.(id); }} />}
      {staffingPort && personnelSessions.map(session => session.kind
        ? <WorkerRelationshipTransitionDialog key={session.id} open={session.open} kind={session.kind} worker={session.worker} asOf={session.asOf} port={staffingPort} fixtureMode={fixtureMode} onClose={retain => closePersonnel(session.id, retain)} onChanged={personnelChanged} onOpenWorkflow={canOpenWorkflowRequests ? reference => route.openWorkflowRequest(reference.id) : undefined} />
        : <WorkerPersonnelActionDialog key={session.id} open={session.open} action={session.action!} worker={session.worker} asOf={session.asOf} port={staffingPort} fixtureMode={fixtureMode} correction={session.correction} remedyLink={(remedy, context) => ((remedy === 'WORKFLOW_REQUESTS' && !access.canReadWorkflowOperations) || (remedy.startsWith('WORKER_') && !canReadRewards) || (remedy.startsWith('ABSENCE_') && !access.canReadAbsence)) ? undefined : route.personnelRemedyLink?.(remedy, context)} onClose={retain => closePersonnel(session.id, retain)} onChanged={personnelChanged} onOpenWorkflow={canOpenWorkflowRequests ? reference => route.openWorkflowRequest(reference.id) : undefined} />)}
      {selected && contractApi && <EmploymentContractChangesDialog open={contractOpen} workerId={selected.id} workerName={selected.name} api={contractApi} initialAsOf={asOf} canCancel={Boolean(access.canCancelRewards)} onOpenWorkflowRequest={canOpenWorkflowRequests ? requestId => route.openWorkflowRequest(requestId) : undefined} canRead={canReadRewards} canWrite={Boolean(access.canWriteRewards)} canApprove={Boolean(access.canApproveRewards)} fixtureMode={fixtureMode} onClose={() => { setContractOpen(false); refreshPersonnel(); }} onChanged={personnelChanged} />}
      {bulkChangeWorkers && bulkPort && <PeopleBulkChangeDialog workers={bulkChangeWorkers} availableWorkers={values.filter(worker => worker.status === '재직' || worker.status === '휴직')} asOf={asOf} port={port} bulkPort={bulkPort} fixtureMode={fixtureMode} onClose={() => setBulkChangeWorkers(undefined)} onOpenJob={(id, receipt) => { setBulkChangeWorkers(undefined); route.openBulkJob?.(id, 'STAFFING_CHANGE', receipt); }} />}
      <FeedbackSnackbar {...feedbackProps} />
      {selected && <PersonNameChangeDialog
        open={nameOpen}
        port={profilePort}
        personId={selected.personId}
        workerName={selected.name}
        workerNumber={selected.workerNumber}
        fixtureMode={fixtureMode}
        fixtureProfile={fixtureNameProfiles[selected.personId] ?? fixturePersonNameProfile(selected)}
        onClose={() => setNameOpen(false)}
        onSaved={(profile) => {
          if (fixtureMode) {
            setFixtureNameProfiles((current) => ({ ...current, [profile.person.id]: profile }));
          }
          setNameOpen(false);
          notify(`${formatPersonDisplayName(profile.preferredName?.fullName, profile.legalName.fullName)}님의 이름을 변경했습니다.`, { tone: 'success' });
          nameProfileResource.reload();
          if (!fixtureMode) resource.reload();
        }}
      />}
      {selected && <ContactChangeDialog
        open={contactOpen}
        initialSection={contactSection}
        port={profilePort}
        worker={selected}
        profile={selectedPrivateProfile}
        fixtureMode={fixtureMode}
        onClose={() => setContactOpen(false)}
        onSubmitted={(message, workflows = []) => { setContactOpen(false); setContactNotice(message); setContactWorkflows(workflows); }}
      />}
      {confirmationDialog}
      <DialogSurface
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        busy={createSubmitting}
        title="구성원 등록"
        help="입력은 이 탭에 임시 보관되며 로그아웃하면 삭제됩니다. 제출하면 입사 승인 프로세스가 시작되며 승인 후 구성원 목록과 조직도에 반영됩니다."
        closeLabel="구성원 등록 닫기"
        size="large"
        mobilePresentation="fullscreen"
        headerContent={<FlowSteps activeStep={createStep} ariaLabel="구성원 등록 단계" steps={['개인 정보', '입사 정보', '확인·제출']} />}
        actions={<>
          {!createdPersonID && !createdHireID && !retryOnly && <Button color="inherit" onClick={() => void discardCreateDraft()} disabled={createSubmitting}>작성 내용 버리기</Button>}
          <Button onClick={() => createStep ? setCreateStep(createStep - 1) : setCreateOpen(false)} disabled={createSubmitting || retryOnly || Boolean(createdHireID) || (createStep === 1 && Boolean(createdPersonID))}>{createStep ? '이전' : '나중에 계속'}</Button>
          {createStep < 2 ? <Button variant="contained" onClick={nextCreateStep} disabled={hireLocked || (createStep === 1 && (createReferenceLoading || Boolean(createReferenceError)))}>다음</Button>
            : <Button variant="contained" onClick={submitHire} disabled={createSubmitting || (!retryOnly && (createReferenceLoading || Boolean(createReferenceError)))}>{createSubmitting ? '제출 중' : (createdPersonID || createdHireID || retryOnly ? '현재 단계 재시도' : '입사 승인 요청')}</Button>}
        </>}
      >
        <Stack sx={{ gap: `${designTokens.layout.pageSectionGroupGap}px`, pt: 1 }}>
          <FormErrorSummary items={createSummaryItems} focusRequest={createErrorFocusRequest} />
          {recoveredDraft && retryOnly && !createError && !createSubmitting && <Alert severity="info">이전에 제출하던 요청을 복구했습니다. 현재 단계 재시도로 같은 요청의 처리 결과를 확인해 주세요.</Alert>}
          {createError && <Alert severity="error">{createError}{createdPersonID && <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>{createdHireID ? '입사 요청 초안이 생성되어 같은 요청으로 계속 진행할 수 있습니다.' : '구성원 기본 정보가 생성되어 같은 요청으로 계속 진행할 수 있습니다.'}</Typography>}</Alert>}
          {createStep !== 0 && createReferenceLoading && <LoadingIndicator label="입사 기준정보 확인 중" />}
          {createReferenceError && <Alert severity="error" action={<Button color="inherit" onClick={() => setReferenceRefresh((value) => value + 1)}>다시 불러오기</Button>}>{createReferenceError}</Alert>}
          {createStep === 0 && <>
            <FormSection heading="이름"><FormGrid>
              <FormTextField required fullWidth label="법적 이름 · 성" value={legalFamilyName} {...personFieldFeedback('legalFamilyName')} onChange={(event) => setLegalFamilyName(event.target.value)} />
              <FormTextField required fullWidth label="법적 이름 · 이름" value={legalGivenName} {...personFieldFeedback('legalGivenName')} onChange={(event) => setLegalGivenName(event.target.value)} />
            </FormGrid></FormSection>
            <FormSection heading="연락처 (선택)"><FormGrid>
              <FormTextField fullWidth type="email" label="개인 이메일" value={homeEmail} {...personFieldFeedback('homeEmail')} onChange={(event) => setHomeEmail(event.target.value)} slotProps={{ htmlInput: { maxLength: 320 } }} />
              <FormTextField fullWidth type="tel" label="휴대폰 연락처" value={homePhone} {...personFieldFeedback('homePhone')} onChange={(event) => setHomePhone(event.target.value)} slotProps={{ htmlInput: { maxLength: 50, inputMode: 'tel' } }} />
            </FormGrid></FormSection>
            <Accordion expanded={identityExpanded} onChange={(_, expanded) => setIdentityExpanded(expanded)}
              slotProps={{ transition: { onEntered: () => focusExpandedPersonField('identity') } }}>
              <AccordionSummary expandIcon={<ExpandMoreRounded />} aria-controls={`${createFormId}-identity-content`} id={`${createFormId}-identity-toggle`}><Typography sx={{ fontWeight: 600 }}>영문 이름·닉네임·계정 ID</Typography></AccordionSummary>
              <AccordionDetails><FormGrid>
                <FormTextField fullWidth label="법적 이름(영문) · 성" value={legalEnglishFamilyName} {...personFieldFeedback('legalEnglishFamilyName')} onChange={(event) => setLegalEnglishFamilyName(event.target.value)} />
                <FormTextField fullWidth label="법적 이름(영문) · 이름" value={legalEnglishGivenName} {...personFieldFeedback('legalEnglishGivenName')} onChange={(event) => setLegalEnglishGivenName(event.target.value)} />
                <FormTextField fullWidth label="사내에서 사용하는 닉네임(한글)" value={nicknameKorean} {...personFieldFeedback('nicknameKorean')} onChange={(event) => setNicknameKorean(event.target.value)} />
                <FormTextField fullWidth label="사내에서 사용하는 닉네임(영문)" value={nicknameEnglish} {...personFieldFeedback('nicknameEnglish')} onChange={(event) => setNicknameEnglish(event.target.value)} />
                <FormGridItem wide><FormTextField fullWidth label="아이디 (이메일 ID)" value={systemID} {...personFieldFeedback('systemID')} onChange={(event) => setSystemID(event.target.value.toLowerCase())} characterCount={{ current: [...systemID.trim()].length, max: 21 }} helperText={personFieldFeedback('systemID').helperText ?? '영문 소문자·숫자·점·밑줄·하이픈, 21자 이내'} /></FormGridItem>
              </FormGrid></AccordionDetails>
            </Accordion>
            <Accordion expanded={privateExpanded} onChange={(_, expanded) => setPrivateExpanded(expanded)}
              slotProps={{ transition: { onEntered: () => focusExpandedPersonField('private') } }}>
              <AccordionSummary expandIcon={<ExpandMoreRounded />} aria-controls={`${createFormId}-private-content`} id={`${createFormId}-private-toggle`}><Typography sx={{ fontWeight: 600 }}>추가 개인정보</Typography></AccordionSummary>
              <AccordionDetails><FormGrid>
                <FormTextField fullWidth type="date" label="생년월일" value={birthDate} {...personFieldFeedback('birthDate')} onChange={(event) => setBirthDate(event.target.value)} slotProps={{ htmlInput: { max: today } }} />
                <FormTextField fullWidth select label="성별" value={gender} onChange={(event) => setGender(event.target.value as typeof gender)}><MenuItem value="">선택 안 함</MenuItem><MenuItem value="MALE">남성</MenuItem><MenuItem value="FEMALE">여성</MenuItem></FormTextField>
                <FormGridItem wide><FormTextField fullWidth label="집 주소" value={homeAddress} {...personFieldFeedback('homeAddress')} onChange={(event) => setHomeAddress(event.target.value)} placeholder="도로명, 건물명, 상세 주소" slotProps={{ htmlInput: { maxLength: 500 } }} /></FormGridItem>
                <FormTextField fullWidth label="시·도" value={homeAddressRegion} onChange={(event) => setHomeAddressRegion(event.target.value)} slotProps={{ htmlInput: { maxLength: 100 } }} />
                <FormTextField fullWidth label="시·군·구" value={homeAddressCity} onChange={(event) => setHomeAddressCity(event.target.value)} slotProps={{ htmlInput: { maxLength: 100 } }} />
                <FormTextField fullWidth label="우편번호" value={homeAddressPostalCode} {...personFieldFeedback('homeAddressPostalCode')} onChange={(event) => setHomeAddressPostalCode(event.target.value)} slotProps={{ htmlInput: { maxLength: 5, inputMode: 'numeric' } }} />
              </FormGrid></AccordionDetails>
            </Accordion>
          </>}
          {createStep === 1 && <>
            <FormSection heading="입사 일정"><FormGrid>
              <FormTextField required fullWidth type="date" label="입사일" value={hireDate} {...hireFieldFeedback('hireDate')} onChange={(event) => { setHireDate(event.target.value); setReferenceNotice('입사일 기준으로 선택 가능한 정보를 확인했습니다. 유효한 선택은 유지하고, 사용할 수 없는 항목만 비웠습니다.'); }} />
              <FormTextField fullWidth select label="구성원 유형" value={hireWorkerType} onChange={(event) => { setHireWorkerType(event.target.value as typeof hireWorkerType); setWorkerNumber(''); setExternalEmployerName(''); setInternalSponsorWorkerID(''); setAccessReviewOn(''); }}><MenuItem value="EMPLOYEE">임직원</MenuItem><MenuItem value="CONTINGENT_WORKER">외부 인력</MenuItem></FormTextField>
            </FormGrid></FormSection>
            {referenceNotice && !createReferenceLoading && !createReferenceError && <Alert severity="info" onClose={() => setReferenceNotice('')}>{referenceNotice}</Alert>}
            <FormSection heading="소속 및 근무 정보"><FormGrid>
              <HireReferenceField required label="회사" value={companyID} options={hireReferences.companies} {...hireFieldFeedback('companyID')} onChange={setCompanyID} disabled={hireReferenceLoading} />
              <HireReferenceField required label="조직" value={organizationID} options={hireOrganizationOptions} {...hireFieldFeedback('organizationID')} onChange={setOrganizationID} disabled={hireReferenceLoading} />
              <HireReferenceField required label="직무" value={jobProfileID} options={hireReferences.jobs} {...hireFieldFeedback('jobProfileID')} onChange={setJobProfileID} disabled={hireReferenceLoading} />
              <HireReferenceField required label="근무지" value={locationID} options={hireReferences.locations} {...hireFieldFeedback('locationID')} onChange={setLocationID} disabled={hireReferenceLoading} />
              <HireReferenceField required label={jobTypeLabel} value={jobTypeID} options={jobTypeReferences} {...hireFieldFeedback('jobTypeID')} onChange={setJobTypeID} disabled={jobTypeReferenceLoading} />
              <FormTextField fullWidth select label="근무 형태" value={hireTimeType} onChange={(event) => setHireTimeType(event.target.value as typeof hireTimeType)}><MenuItem value="FULL_TIME">전일제</MenuItem><MenuItem value="PART_TIME">시간제</MenuItem></FormTextField>
            </FormGrid></FormSection>
            {hireWorkerType === 'EMPLOYEE' ? <Typography variant="body2" color="text.secondary">사번은 회사의 발급 정책에 따라 자동 발급됩니다.</Typography> : <>
              <FormSection heading="외부 인력 정보" help="외부 인력은 로그인 계정이 자동 생성되지 않으며 Google Workspace 계정도 기본 생성하지 않습니다."><FormGrid>
                <FormTextField required label="외부 인력 번호" value={workerNumber} {...hireFieldFeedback('workerNumber')} onChange={(event) => setWorkerNumber(event.target.value)} />
                <FormTextField required label="외부 소속사" value={externalEmployerName} {...hireFieldFeedback('externalEmployerName')} onChange={(event) => setExternalEmployerName(event.target.value)} />
                <HireReferenceField required label="내부 스폰서" value={internalSponsorWorkerID} options={values.filter((worker) => worker.status === '재직').map((worker) => ({ id: worker.id, descriptor: `${worker.name} · ${worker.organization} · ${worker.title}` }))} {...hireFieldFeedback('internalSponsorWorkerID')} onChange={setInternalSponsorWorkerID} />
                <FormTextField required type="date" label="접근 권한 검토일" value={accessReviewOn} {...hireFieldFeedback('accessReviewOn')} onChange={(event) => setAccessReviewOn(event.target.value)} />
              </FormGrid></FormSection>
            </>}
            <Accordion expanded={assignmentExpanded} onChange={(_, expanded) => setAssignmentExpanded(expanded)}>
              <AccordionSummary expandIcon={<ExpandMoreRounded />}><Typography sx={{ fontWeight: 600 }}>추가 배정 정보</Typography></AccordionSummary>
              <AccordionDetails><FormGrid>
                <HireReferenceField label="업무 공간 (선택)" value={workSpaceID} options={workSpaceReferences} onChange={setWorkSpaceID} disabled={!locationID || workSpaceReferenceLoading} helperText={!locationID ? '근무지를 먼저 선택해 주세요.' : undefined} />
                <HireReferenceField label="직무 레벨 (선택)" value={jobLevelID} options={hireReferences.levels} onChange={setJobLevelID} disabled={hireReferenceLoading} help="승진·보상 체계를 사용하는 경우 선택합니다." />
                <FormGridItem wide><BusinessTitleField value={businessTitle} onChange={setBusinessTitle} jobProfile={businessTitleReferencesReady ? hireJobProfile?.descriptor : undefined} jobLevel={businessTitleReferencesReady ? hireJobLevel?.descriptor : undefined} disabled={!businessTitleReferencesReady} helperText="표시용 명칭 예: Senior Backend Engineer" /></FormGridItem>
              </FormGrid></AccordionDetails>
            </Accordion>
          </>}
          {createStep === 2 && <>
            <Stack component="section" aria-labelledby={`${createFormId}-personal-review`} spacing={2}>
              <Typography id={`${createFormId}-personal-review`} component="h3" sx={designTokens.typography.subsectionTitle}>개인 정보</Typography>
              <DefinitionGrid items={[
                { label: '법적 이름', value: legalFullName },
                ...optionalRegistrationDetail('법적 이름(영문)', legalEnglishFullName),
                ...optionalRegistrationDetail('닉네임', [nicknameKorean, nicknameEnglish].filter(Boolean).join(' · ')),
                ...optionalRegistrationDetail('아이디 (이메일 ID)', systemID),
                ...optionalRegistrationDetail('생년월일', birthDate),
                ...optionalRegistrationDetail('성별', gender ? genderLabel : ''),
                ...optionalRegistrationDetail('개인 이메일', homeEmail),
                ...optionalRegistrationDetail('휴대폰 연락처', homePhone),
                ...optionalRegistrationDetail('집 주소', homeAddressLabel),
              ]} />
            </Stack>
            <Stack component="section" aria-labelledby={`${createFormId}-employment-review`} spacing={2}>
              <Typography id={`${createFormId}-employment-review`} component="h3" sx={designTokens.typography.subsectionTitle}>입사 정보</Typography>
              <DefinitionGrid items={[
                { label: '입사일', value: hireDate },
                { label: '회사', value: hireReferences.companies.find((item) => item.id === companyID)?.descriptor || '—' },
                { label: '조직', value: hireOrganizationOptions.find((item) => item.id === organizationID)?.descriptor || '—' },
                { label: '직무', value: hireJobProfile?.descriptor || '—' },
                { label: '근무지', value: hireReferences.locations.find((item) => item.id === locationID)?.descriptor || '—' },
                { label: '구성원 유형', value: hireWorkerType === 'EMPLOYEE' ? '임직원' : '외부 인력' },
                { label: jobTypeLabel, value: jobTypeReferences.find((item) => item.id === jobTypeID)?.descriptor || '—' },
                { label: '근무 형태', value: hireTimeType === 'FULL_TIME' ? '전일제' : '시간제' },
                { label: hireWorkerType === 'EMPLOYEE' ? '사번' : '외부 인력 번호', value: hireWorkerType === 'EMPLOYEE' ? '자동 발급' : workerNumber },
                ...optionalRegistrationDetail('업무 공간', workSpaceReferences.find((item) => item.id === workSpaceID)?.descriptor || ''),
                ...optionalRegistrationDetail('직무 레벨', hireJobLevel?.descriptor || ''),
                ...optionalRegistrationDetail('직함', businessTitle),
                ...(hireWorkerType === 'CONTINGENT_WORKER' ? [
                  { label: '외부 소속사', value: externalEmployerName },
                  { label: '내부 스폰서', value: values.find((worker) => worker.id === internalSponsorWorkerID)?.name || '—' },
                  { label: '접근 권한 검토일', value: accessReviewOn },
                  { label: '계정 정책', value: '로그인·Google Workspace 계정 자동 생성 안 함' },
                ] : []),
              ]} />
            </Stack>
          </>}
        </Stack>
      </DialogSurface>
    </>
  );
}

function WorkerDetail({
  worker,
  asOf,
  nameProfile,
  nameState,
  privateProfile,
  privateLoading,
  employmentProfile,
  employmentLoading,
  employmentError,
  changes,
  profilePort,
  fixtureMode,
  canManagePhoto,
  canReadFamily,
  canWriteFamily,
  canApproveFamily,
  canReadCareer,
  canWriteCareer,
  canApproveCareer,
  onEditName,
  onEditContact,
  onStartStaffing,
  personnelActions,
  onOpenOrganizationStaffing,
  onPhotoChanged,
}: {
  worker: WorkerProfileWorker;
  asOf: string;
  nameProfile?: PersonNameProfile;
  nameState: 'loading' | 'ready' | 'forbidden' | 'error' | 'unavailable';
  privateProfile: WorkerPrivateProfile;
  privateLoading: boolean;
  employmentProfile: WorkerEmploymentProfile;
  employmentLoading: boolean;
  employmentError: boolean;
  changes: StaffingChangeSummary[];
  profilePort: PeopleProfilePort;
  fixtureMode: boolean;
  canManagePhoto: boolean;
  canReadFamily: boolean;
  canWriteFamily: boolean;
  canApproveFamily: boolean;
  canReadCareer: boolean;
  canWriteCareer: boolean;
  canApproveCareer: boolean;
  onEditName?: () => void;
  onEditContact?: (section?: 'HOME' | 'WORK' | 'EMERGENCY') => void;
  onStartStaffing?: () => void;
  personnelActions?: ReactNode;
  onOpenOrganizationStaffing?: () => void;
  onPhotoChanged: () => void;
}) {
  const photoControl = canManagePhoto ? <ProfilePhotoControl port={profilePort} personId={worker.personId} name={worker.name} photoUrl={worker.photoUrl} fixtureMode={fixtureMode} presentation="profileHeader" onChanged={onPhotoChanged} /> : undefined;
  const familyContent = canReadFamily && worker.personId ? <PersonFamilySection port={profilePort} personId={worker.personId} canRead canWrite={canWriteFamily} canApprove={canApproveFamily} fixtureMode={fixtureMode} /> : undefined;
  const careerContent = canReadCareer && worker.personId ? <ExternalCareerSection port={profilePort} personId={worker.personId} canRead canWrite={canWriteCareer} canApprove={canApproveCareer} fixtureMode={fixtureMode} /> : undefined;
  return <WorkerProfile worker={{ ...worker, phone: maskPhone(worker.phone) }} asOf={asOf} nameProfile={nameProfile} nameState={nameState} privateProfile={privateProfile} privateLoading={privateLoading} employmentProfile={employmentProfile} employmentLoading={employmentLoading} employmentError={employmentError} changes={changes} photoControl={photoControl} familyContent={familyContent} careerContent={careerContent} historyContent={!fixtureMode && profilePort.listPersonHistory ? <PersonChangeHistory personId={worker.personId} port={profilePort} /> : undefined} onEditName={onEditName} onEditContact={onEditContact} onStartStaffing={onStartStaffing} personnelActions={personnelActions} onOpenOrganizationStaffing={onOpenOrganizationStaffing} />;
}

function maskPhone(value: string) { return value.replace(/(\d{3})-(\d{4})-(\d{4})/, '$1-****-$3'); }
